import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { 
  FaCalculator, FaWallet, FaMoneyBillWave, FaChartPie, FaExclamationTriangle,
  FaCheckCircle, FaPlus, FaTrash, FaArrowUp, FaArrowDown, FaSave,
  FaChevronLeft, FaChevronRight, FaHeart, FaHome, FaCar, FaUsers,
  FaPrayingHands, FaCreditCard, FaSpinner, FaInfoCircle, FaTrophy,
  FaBullseye, FaSearch, FaLightbulb, FaGraduationCap, FaUmbrella,
  FaChartLine, FaShieldAlt, FaUtensils, FaShoppingCart, FaTv,
  FaUserFriends, FaBell, FaFileInvoiceDollar, FaPiggyBank, FaGift, FaThumbsUp,
  FaEye, FaUserCircle, FaFilePdf, FaDownload
} from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// India-specific income types
const INCOME_TYPES = {
  salary: "Salary (Net Take Home)",
  business: "Business / Professional Income",
  rental: "Rental Income",
  commission: "Commission / Incentives",
  side_income: "Side Income / Freelancing",
  family: "Family Contributions",
  irregular: "Irregular Income (Bonus, Festival)"
};

// India-specific expense categories
const EXPENSE_CATEGORIES = {
  household: {
    name: "Household Essentials",
    icon: FaHome,
    color: "bg-blue-500",
    subcategories: {
      rent: "Rent / Home Loan EMI",
      groceries: "Groceries",
      utilities: "Electricity / Gas / Water",
      telecom: "Mobile / Internet / DTH",
      domestic_help: "Maid / Driver Salary"
    }
  },
  lifestyle: {
    name: "Lifestyle & Mobility",
    icon: FaCar,
    color: "bg-purple-500",
    subcategories: {
      fuel: "Fuel",
      vehicle_emi: "Vehicle EMI",
      maintenance: "Maintenance",
      transport: "Public Transport",
      travel: "Travel & Vacation"
    }
  },
  family: {
    name: "Family Responsibilities",
    icon: FaUsers,
    color: "bg-green-500",
    subcategories: {
      education: "School Fees / Tuition",
      medical: "Medical Expenses",
      parents_support: "Parents' Support",
      insurance_premium: "Insurance Premiums",
      social: "Festival & Social Functions"
    }
  },
  cultural: {
    name: "Cultural & Occasional",
    icon: FaPrayingHands,
    color: "bg-orange-500",
    subcategories: {
      weddings: "Weddings",
      donations: "Religious Donations",
      gifting: "Gifting",
      gold: "Gold Purchases"
    }
  },
  financial: {
    name: "Financial Commitments",
    icon: FaCreditCard,
    color: "bg-red-500",
    subcategories: {
      credit_card: "Credit Card Payments",
      personal_loan: "Personal Loan EMI",
      sip: "SIPs",
      lic: "LIC Premium",
      chit_fund: "Chit Funds / Committees"
    }
  }
};

const MoneyMeter = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  
  // Client selection for advisors/managers
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const isAdvisorOrManager = user?.role === 'advisor' || user?.role === 'manager';
  
  // Budget State
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [emergencyFundCurrent, setEmergencyFundCurrent] = useState(0);
  const [healthScore, setHealthScore] = useState(null);
  const [budgetSummary, setBudgetSummary] = useState(null);
  
  // Phase 2 State
  const [goalData, setGoalData] = useState(null);
  const [goalAllocations, setGoalAllocations] = useState({});
  const [savingGoals, setSavingGoals] = useState(false);
  const [cashLeaks, setCashLeaks] = useState(null);
  
  // Phase 3 State
  const [familyData, setFamilyData] = useState(null);
  const [behaviouralInsights, setBehaviouralInsights] = useState(null);
  const [taxData, setTaxData] = useState(null);
  
  // UI State
  const [activeTab, setActiveTab] = useState('income');
  const [showAddIncome, setShowAddIncome] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const pdfRef = useRef(null);
  
  // New Entry State
  const [newIncome, setNewIncome] = useState({ type: 'salary', amount: '', description: '', is_regular: true });
  const [newExpense, setNewExpense] = useState({ category: 'household', subcategory: 'rent', amount: '', description: '', is_emi: false });
  
  // Family Dashboard State
  const [familyOptions, setFamilyOptions] = useState(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showAddInsurance, setShowAddInsurance] = useState(false);
  const [showAddNominee, setShowAddNominee] = useState(false);
  const [showAddSharedExpense, setShowAddSharedExpense] = useState(false);
  const [savingFamily, setSavingFamily] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', relation: 'Spouse', age: '', is_earning: false, income_contribution: '', occupation: '' });
  const [newInsurance, setNewInsurance] = useState({ type: 'life', policy_name: '', policy_number: '', provider: '', coverage_amount: '', premium_amount: '', premium_frequency: 'yearly', is_family_floater: false, nominee: '' });
  const [newNominee, setNewNominee] = useState({ asset_type: 'Bank Account', asset_name: '', nominee: '', relation: 'Spouse', share_percentage: 100 });
  const [newSharedExpense, setNewSharedExpense] = useState({ category: 'Rent/Housing', description: '', amount: '', frequency: 'monthly' });

  // Tax Awareness State
  const [showAdd80C, setShowAdd80C] = useState(false);
  const [savingTax, setSavingTax] = useState(false);
  const [new80C, setNew80C] = useState({ key: 'ppf', name: '', description: '', amount: '' });
  const SECTION_80C_OPTIONS = [
    { key: 'ppf', name: 'PPF', description: 'Public Provident Fund' },
    { key: 'epf', name: 'EPF', description: 'Employee Provident Fund contribution' },
    { key: 'elss', name: 'ELSS', description: 'Equity Linked Savings Scheme' },
    { key: 'nsc', name: 'NSC', description: 'National Savings Certificate' },
    { key: 'life_insurance', name: 'Life Insurance', description: 'LIC/Term Insurance Premium' },
    { key: 'ulip', name: 'ULIP', description: 'Unit Linked Insurance Plan' },
    { key: 'sukanya', name: 'Sukanya Samriddhi', description: 'Girl child savings scheme' },
    { key: 'tuition_fees', name: 'Tuition Fees', description: "Children's education fees" },
    { key: 'home_loan_principal', name: 'Home Loan Principal', description: 'Principal repayment' },
    { key: 'tax_saver_fd', name: 'Tax Saver FD', description: '5-year bank fixed deposit' },
    { key: 'other', name: 'Other', description: 'Other 80C eligible investment' }
  ];

  // Get current financial year
  const getCurrentFinancialYear = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    if (month >= 4) {
      return `${year}-${(year + 1).toString().slice(2)}`;
    }
    return `${year - 1}-${year.toString().slice(2)}`;
  };

  // Fetch clients for advisor/manager
  const fetchFamilyOptions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/money-meter/family-mode/options`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFamilyOptions(response.data);
    } catch (err) {
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      let clientsData = [];
      
      if (user?.role === 'advisor') {
        const response = await axios.get(`${API}/advisor/all-clients`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        clientsData = response.data || [];
      } else if (user?.role === 'manager') {
        const response = await axios.get(`${API}/manager/all-clients`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        clientsData = response.data || [];
      }
      
      setClients(clientsData);
    } catch (err) {
    }
  }, [user?.role]);

  const fetchBudgetData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const financialYear = getCurrentFinancialYear();
      
      const clientParam = isAdvisorOrManager && selectedClientId ? `?client_id=${selectedClientId}` : '';
      
      const [budgetRes, scoreRes, goalRes, leaksRes, familyRes, insightsRes, taxRes] = await Promise.all([
        axios.get(`${API}/money-meter/budget/${currentMonth}${clientParam}`, { headers }),
        axios.get(`${API}/money-meter/health-score/${currentMonth}${clientParam}`, { headers }),
        axios.get(`${API}/money-meter/goal-linked-budgeting/${currentMonth}${clientParam}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/money-meter/cash-leaks/${currentMonth}${clientParam}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/money-meter/family-mode/${currentMonth}${clientParam}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/money-meter/behavioural-insights/${currentMonth}${clientParam}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/money-meter/tax-awareness/${financialYear}${clientParam}`, { headers }).catch(() => ({ data: null }))
      ]);
      
      setIncomes(budgetRes.data.incomes || []);
      setExpenses(budgetRes.data.expenses || []);
      setEmergencyFundCurrent(budgetRes.data.emergency_fund_current || 0);
      setBudgetSummary(budgetRes.data);
      setHealthScore(scoreRes.data);
      setGoalData(goalRes.data);
      if (goalRes.data?.goals) {
        const allocations = {};
        goalRes.data.goals.forEach(g => {
          allocations[g.id] = g.actual_monthly || 0;
        });
        setGoalAllocations(allocations);
      }
      setCashLeaks(leaksRes.data);
      setFamilyData(familyRes.data);
      setBehaviouralInsights(insightsRes.data);
      setTaxData(taxRes.data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [currentMonth, selectedClientId, isAdvisorOrManager]);

  useEffect(() => {
    if (isAdvisorOrManager) {
      fetchClients();
    }
  }, [isAdvisorOrManager, fetchClients]);

  useEffect(() => {
    if (!isAdvisorOrManager || selectedClientId) {
      fetchBudgetData();
    }
  }, [fetchBudgetData, isAdvisorOrManager, selectedClientId]);

  useEffect(() => {
    fetchFamilyOptions();
  }, [fetchFamilyOptions]);

  const saveBudget = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/money-meter/budget`, {
        month: currentMonth,
        incomes,
        expenses,
        emergency_fund_current: emergencyFundCurrent
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBudgetSummary(response.data.budget);
      setHealthScore(response.data.health_score);
      
      // Refresh goal and leak data after saving
      const headers = { Authorization: `Bearer ${token}` };
      const [goalRes, leaksRes] = await Promise.all([
        axios.get(`${API}/money-meter/goal-linked-budgeting/${currentMonth}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/money-meter/cash-leaks/${currentMonth}`, { headers }).catch(() => ({ data: null }))
      ]);
      setGoalData(goalRes.data);
      // Initialize goal allocations from fetched data
      if (goalRes.data?.goals) {
        const allocations = {};
        goalRes.data.goals.forEach(g => {
          allocations[g.id] = g.actual_monthly || 0;
        });
        setGoalAllocations(allocations);
      }
      setCashLeaks(leaksRes.data);
    } catch (err) {
    } finally {
      setSaving(false);
    }
  };

  // Save Goal Allocations
  const saveGoalAllocations = async () => {
    setSavingGoals(true);
    try {
      const token = localStorage.getItem('token');
      const allocationsArray = Object.entries(goalAllocations).map(([goal_id, actual_monthly]) => ({
        goal_id,
        actual_monthly: parseFloat(actual_monthly) || 0
      }));
      
      const response = await axios.post(`${API}/money-meter/goal-linked-budgeting/${currentMonth}`, {
        allocations: allocationsArray
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setGoalData(response.data);
    } catch (err) {
    } finally {
      setSavingGoals(false);
    }
  };

  // ========== FAMILY DASHBOARD FUNCTIONS ==========
  
  // Refresh family data
  const refreshFamilyData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/money-meter/family-mode/${currentMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFamilyData(response.data);
    } catch (err) {
    }
  };

  // Add Family Member
  const addFamilyMember = async () => {
    if (!newMember.name || !newMember.relation) return;
    setSavingFamily(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/money-meter/family-mode/member`, {
        ...newMember,
        age: parseInt(newMember.age) || null,
        income_contribution: parseFloat(newMember.income_contribution) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMember({ name: '', relation: 'Spouse', age: '', is_earning: false, income_contribution: '', occupation: '' });
      setShowAddMember(false);
      await refreshFamilyData();
    } catch (err) {
    } finally {
      setSavingFamily(false);
    }
  };

  // Delete Family Member
  const deleteFamilyMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this family member?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/money-meter/family-mode/member/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshFamilyData();
    } catch (err) {
    }
  };

  // Add Insurance Policy
  const addInsurancePolicy = async () => {
    if (!newInsurance.policy_name || !newInsurance.coverage_amount) return;
    setSavingFamily(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/money-meter/family-mode/insurance`, {
        ...newInsurance,
        coverage_amount: parseFloat(newInsurance.coverage_amount) || 0,
        premium_amount: parseFloat(newInsurance.premium_amount) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewInsurance({ type: 'life', policy_name: '', policy_number: '', provider: '', coverage_amount: '', premium_amount: '', premium_frequency: 'yearly', is_family_floater: false, nominee: '' });
      setShowAddInsurance(false);
      await refreshFamilyData();
    } catch (err) {
    } finally {
      setSavingFamily(false);
    }
  };

  // Delete Insurance Policy
  const deleteInsurancePolicy = async (policyId) => {
    if (!window.confirm('Are you sure you want to remove this insurance policy?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/money-meter/family-mode/insurance/${policyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshFamilyData();
    } catch (err) {
    }
  };

  // Add Nominee
  const addNominee = async () => {
    if (!newNominee.asset_name || !newNominee.nominee) return;
    setSavingFamily(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/money-meter/family-mode/nominee`, {
        ...newNominee,
        share_percentage: parseFloat(newNominee.share_percentage) || 100
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewNominee({ asset_type: 'Bank Account', asset_name: '', nominee: '', relation: 'Spouse', share_percentage: 100 });
      setShowAddNominee(false);
      await refreshFamilyData();
    } catch (err) {
    } finally {
      setSavingFamily(false);
    }
  };

  // Delete Nominee
  const deleteNominee = async (nomineeId) => {
    if (!window.confirm('Are you sure you want to remove this nominee?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/money-meter/family-mode/nominee/${nomineeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshFamilyData();
    } catch (err) {
    }
  };

  // Add Shared Expense
  const addSharedExpense = async () => {
    if (!newSharedExpense.description || !newSharedExpense.amount) return;
    setSavingFamily(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/money-meter/family-mode/expense`, {
        ...newSharedExpense,
        amount: parseFloat(newSharedExpense.amount) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewSharedExpense({ category: 'Rent/Housing', description: '', amount: '', frequency: 'monthly' });
      setShowAddSharedExpense(false);
      await refreshFamilyData();
    } catch (err) {
    } finally {
      setSavingFamily(false);
    }
  };

  // Delete Shared Expense
  const deleteSharedExpense = async (expenseId) => {
    if (!window.confirm('Are you sure you want to remove this shared expense?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/money-meter/family-mode/expense/${expenseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshFamilyData();
    } catch (err) {
    }
  };

  // ========== TAX AWARENESS FUNCTIONS ==========
  
  // Refresh tax data
  const refreshTaxData = async () => {
    try {
      const token = localStorage.getItem('token');
      const financialYear = getCurrentFinancialYear();
      const response = await axios.get(`${API}/money-meter/tax-awareness/${financialYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaxData(response.data);
    } catch (err) {
    }
  };

  // Add 80C Component
  const add80CComponent = async () => {
    if (!new80C.amount) return;
    setSavingTax(true);
    try {
      const token = localStorage.getItem('token');
      const financialYear = getCurrentFinancialYear();
      const selectedOption = SECTION_80C_OPTIONS.find(o => o.key === new80C.key);
      await axios.post(`${API}/money-meter/tax-awareness/80c-component`, {
        financial_year: financialYear,
        key: new80C.key,
        name: new80C.name || selectedOption?.name || new80C.key,
        description: new80C.description || selectedOption?.description || '',
        amount: parseFloat(new80C.amount) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNew80C({ key: 'ppf', name: '', description: '', amount: '' });
      setShowAdd80C(false);
      await refreshTaxData();
    } catch (err) {
    } finally {
      setSavingTax(false);
    }
  };

  // Delete 80C Component
  const delete80CComponent = async (componentId) => {
    if (!window.confirm('Are you sure you want to remove this 80C investment?')) return;
    try {
      const token = localStorage.getItem('token');
      const financialYear = getCurrentFinancialYear();
      await axios.delete(`${API}/money-meter/tax-awareness/80c-component/${componentId}?financial_year=${financialYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshTaxData();
    } catch (err) {
    }
  };

  // PDF Generation Function - Comprehensive Money Meter Report
  const generatePDF = async () => {
    setGeneratingPDF(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 12;
      const contentWidth = pageWidth - 2 * margin;
      let yPos = margin;
      let pageNum = 1;

      // Helper functions
      const addText = (text, x, y, options = {}) => {
        const { fontSize = 10, fontStyle = 'normal', color = [0, 0, 0], maxWidth = null, align = 'left' } = options;
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', fontStyle);
        pdf.setTextColor(...color);
        if (maxWidth) {
          pdf.text(text, x, y, { maxWidth, align });
        } else {
          pdf.text(String(text), x, y, { align });
        }
      };

      const addLine = (y, color = [200, 200, 200], thickness = 0.3) => {
        pdf.setDrawColor(...color);
        pdf.setLineWidth(thickness);
        pdf.line(margin, y, pageWidth - margin, y);
      };

      const checkPageBreak = (requiredSpace) => {
        if (yPos + requiredSpace > pageHeight - 20) {
          addFooter();
          pdf.addPage();
          pageNum++;
          yPos = margin + 5;
          return true;
        }
        return false;
      };

      const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN')}`;
      
      const addFooter = () => {
        pdf.setFillColor(15, 23, 42);
        pdf.rect(0, pageHeight - 12, pageWidth, 12, 'F');
        addText('Arth Gurukul | Your School for Lifelong Financial Wellness', margin, pageHeight - 5, { fontSize: 7, color: [150, 150, 150] });
        addText(`Page ${pageNum}`, pageWidth - margin, pageHeight - 5, { fontSize: 7, color: [150, 150, 150], align: 'right' });
      };

      const addSectionHeader = (title, icon = '📊') => {
        checkPageBreak(20);
        pdf.setFillColor(26, 41, 66);
        pdf.roundedRect(margin, yPos, contentWidth, 8, 1, 1, 'F');
        addText(`${icon}  ${title}`, margin + 3, yPos + 5.5, { fontSize: 11, fontStyle: 'bold', color: [255, 215, 0] });
        yPos += 12;
      };

      const addDataRow = (label, value, options = {}) => {
        const { labelColor = [100, 100, 100], valueColor = [30, 30, 30], bold = false } = options;
        addText(label, margin + 2, yPos, { fontSize: 9, color: labelColor });
        addText(String(value), pageWidth - margin - 2, yPos, { fontSize: 9, fontStyle: bold ? 'bold' : 'normal', color: valueColor, align: 'right' });
        yPos += 5;
      };

      const getScoreColorPDF = (score) => {
        if (score >= 80) return [34, 197, 94];
        if (score >= 60) return [59, 130, 246];
        if (score >= 40) return [251, 191, 36];
        return [239, 68, 68];
      };

      // ==================== PAGE 1: HEADER & EXECUTIVE SUMMARY ====================
      
      // Header Banner
      pdf.setFillColor(15, 23, 42);
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      // Gold accent line
      pdf.setFillColor(255, 215, 0);
      pdf.rect(0, 38, pageWidth, 2, 'F');
      
      addText('MONEY METER', margin, 14, { fontSize: 22, fontStyle: 'bold', color: [255, 215, 0] });
      addText('Comprehensive Financial Health Report', margin, 22, { fontSize: 11, color: [200, 200, 200] });
      
      const reportDate = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
      addText(`Report Period: ${formatMonth(currentMonth)}`, pageWidth - margin, 14, { fontSize: 9, color: [150, 150, 150], align: 'right' });
      addText(`Generated: ${reportDate}`, pageWidth - margin, 20, { fontSize: 9, color: [150, 150, 150], align: 'right' });
      
      if (isAdvisorOrManager && selectedClient) {
        addText(`Client: ${selectedClient.name}`, pageWidth - margin, 26, { fontSize: 10, fontStyle: 'bold', color: [255, 215, 0], align: 'right' });
        addText(selectedClient.email || '', pageWidth - margin, 32, { fontSize: 8, color: [150, 150, 150], align: 'right' });
      }
      
      yPos = 48;

      // ==================== MONEY FITNESS SCORE CARD ====================
      const score = healthScore?.overall_score || 0;
      const rating = healthScore?.rating || 'Not Available';
      
      pdf.setFillColor(26, 41, 66);
      pdf.roundedRect(margin, yPos, contentWidth, 50, 3, 3, 'F');
      
      // Score circle
      const scoreColor = getScoreColorPDF(score);
      pdf.setFillColor(...scoreColor);
      pdf.circle(margin + 20, yPos + 25, 15, 'F');
      pdf.setFillColor(15, 23, 42);
      pdf.circle(margin + 20, yPos + 25, 12, 'F');
      addText(String(Math.round(score)), margin + 20 - (score >= 100 ? 7 : score >= 10 ? 5 : 3), yPos + 28, { fontSize: 16, fontStyle: 'bold', color: scoreColor });
      
      addText('Money Fitness Rating', margin + 42, yPos + 12, { fontSize: 14, fontStyle: 'bold', color: [255, 255, 255] });
      addText(rating, margin + 42, yPos + 20, { fontSize: 12, fontStyle: 'bold', color: scoreColor });
      
      // Score interpretation
      const scoreInterpretation = score >= 80 ? 'Excellent! Your finances are well-managed.' :
                                  score >= 60 ? 'Good progress. Few areas need attention.' :
                                  score >= 40 ? 'Average. Multiple areas need improvement.' :
                                  'Critical! Immediate action required.';
      addText(scoreInterpretation, margin + 42, yPos + 28, { fontSize: 9, color: [180, 180, 180] });
      
      // Individual scores grid
      const scoreItems = [
        { label: 'Savings', value: healthScore?.savings_score || 0, desc: savingsRate >= 20 ? 'Healthy' : 'Low' },
        { label: 'EMI', value: healthScore?.emi_score || 0, desc: emiRatio < 40 ? 'Safe' : 'High' },
        { label: 'Emergency', value: healthScore?.emergency_score || 0, desc: (budgetSummary?.emergency_fund_coverage || 0) >= 6 ? 'Covered' : 'Build' },
        { label: 'Investment', value: healthScore?.investment_score || 0, desc: (budgetSummary?.investments_actual || 0) > 0 ? 'Active' : 'Start' },
        { label: 'Insurance', value: healthScore?.insurance_score || 0, desc: (budgetSummary?.protection_actual || 0) > 0 ? 'Protected' : 'Get' },
        { label: 'Lifestyle', value: healthScore?.lifestyle_score || 0, desc: (budgetSummary?.lifestyle_actual || 0) <= budgetAllocation.lifestyle ? 'Controlled' : 'High' }
      ];
      
      let scoreX = margin + 42;
      scoreItems.forEach((s, i) => {
        const col = i % 3;
        const row = Math.floor(i / 3);
        const x = scoreX + (col * 40);
        const y = yPos + 35 + (row * 10);
        const sColor = getScoreColorPDF(s.value);
        addText(s.label, x, y, { fontSize: 7, color: [150, 150, 150] });
        addText(`${Math.round(s.value)}`, x + 25, y, { fontSize: 8, fontStyle: 'bold', color: sColor });
      });
      
      yPos += 58;

      // ==================== FINANCIAL SNAPSHOT ====================
      addSectionHeader('Financial Snapshot', '💰');
      
      // Summary boxes
      const summaryItems = [
        { label: 'Total Income', value: formatCurrency(totalIncome), color: [34, 197, 94] },
        { label: 'Total Expenses', value: formatCurrency(totalExpenses), color: [239, 68, 68] },
        { label: 'Net Savings', value: formatCurrency(netSavings), color: netSavings >= 0 ? [34, 197, 94] : [239, 68, 68] },
        { label: 'Savings Rate', value: `${savingsRate.toFixed(1)}%`, color: savingsRate >= 20 ? [34, 197, 94] : [251, 191, 36] }
      ];
      
      const boxWidth = (contentWidth - 9) / 4;
      summaryItems.forEach((item, i) => {
        const x = margin + (i * (boxWidth + 3));
        pdf.setFillColor(240, 240, 245);
        pdf.roundedRect(x, yPos, boxWidth, 18, 2, 2, 'F');
        addText(item.label, x + 3, yPos + 6, { fontSize: 7, color: [100, 100, 100] });
        addText(item.value, x + 3, yPos + 13, { fontSize: 10, fontStyle: 'bold', color: item.color });
      });
      yPos += 24;

      // ==================== BUDGET RULE (50-20-20-10) ====================
      addSectionHeader('India-Optimized Budget Rule (50-20-20-10)', '📊');
      
      const allocations = [
        { label: 'Essentials', percent: 50, budget: budgetAllocation.essentials, actual: budgetSummary?.essentials_actual || 0, icon: '🏠' },
        { label: 'Lifestyle', percent: 20, budget: budgetAllocation.lifestyle, actual: budgetSummary?.lifestyle_actual || 0, icon: '🎯' },
        { label: 'Investments', percent: 20, budget: budgetAllocation.investments, actual: budgetSummary?.investments_actual || 0, icon: '📈' },
        { label: 'Protection', percent: 10, budget: budgetAllocation.protection, actual: budgetSummary?.protection_actual || 0, icon: '🛡️' }
      ];
      
      // Table header
      pdf.setFillColor(240, 240, 245);
      pdf.rect(margin, yPos, contentWidth, 7, 'F');
      addText('Category', margin + 2, yPos + 5, { fontSize: 8, fontStyle: 'bold', color: [60, 60, 60] });
      addText('Benchmark', margin + 55, yPos + 5, { fontSize: 8, fontStyle: 'bold', color: [60, 60, 60] });
      addText('Actual', margin + 95, yPos + 5, { fontSize: 8, fontStyle: 'bold', color: [60, 60, 60] });
      addText('Variance', margin + 130, yPos + 5, { fontSize: 8, fontStyle: 'bold', color: [60, 60, 60] });
      addText('Status', pageWidth - margin - 15, yPos + 5, { fontSize: 8, fontStyle: 'bold', color: [60, 60, 60] });
      yPos += 9;
      
      allocations.forEach((alloc) => {
        const actualPercent = totalIncome > 0 ? Math.round((alloc.actual / totalIncome) * 100) : 0;
        const variance = alloc.actual - alloc.budget;
        const isInvestOrProtect = alloc.label === 'Investments' || alloc.label === 'Protection';
        const isOnTrack = isInvestOrProtect ? (actualPercent >= alloc.percent) : (actualPercent <= alloc.percent);
        const statusText = isOnTrack ? '✓ On Track' : (isInvestOrProtect ? '↑ Below' : '↓ Over');
        const statusColor = isOnTrack ? [34, 197, 94] : [239, 68, 68];
        
        addText(`${alloc.icon} ${alloc.label} (${alloc.percent}%)`, margin + 2, yPos + 4, { fontSize: 8, color: [60, 60, 60] });
        addText(formatCurrency(alloc.budget), margin + 55, yPos + 4, { fontSize: 8, color: [100, 100, 100] });
        addText(formatCurrency(alloc.actual), margin + 95, yPos + 4, { fontSize: 8, fontStyle: 'bold', color: [30, 30, 30] });
        addText(`${variance >= 0 ? '+' : ''}${formatCurrency(variance)}`, margin + 130, yPos + 4, { fontSize: 8, color: variance > 0 ? (isInvestOrProtect ? [34, 197, 94] : [239, 68, 68]) : (isInvestOrProtect ? [239, 68, 68] : [34, 197, 94]) });
        addText(statusText, pageWidth - margin - 2, yPos + 4, { fontSize: 8, fontStyle: 'bold', color: statusColor, align: 'right' });
        yPos += 7;
      });
      yPos += 5;

      // ==================== INCOME & EXPENSES ====================
      checkPageBreak(50);
      addSectionHeader('Income & Expense Analysis', '💳');
      
      // Two columns: Income | Expenses
      const colWidth = (contentWidth - 5) / 2;
      
      // Income column
      pdf.setFillColor(34, 197, 94, 30);
      pdf.roundedRect(margin, yPos, colWidth, 35, 2, 2, 'F');
      addText('Income Sources', margin + 3, yPos + 6, { fontSize: 9, fontStyle: 'bold', color: [34, 150, 94] });
      let incY = yPos + 12;
      incomes.slice(0, 4).forEach((income) => {
        addText(`• ${INCOME_TYPES[income.type] || income.type}`, margin + 3, incY, { fontSize: 8, color: [60, 60, 60] });
        addText(formatCurrency(income.amount), margin + colWidth - 3, incY, { fontSize: 8, fontStyle: 'bold', color: [34, 150, 94], align: 'right' });
        incY += 5;
      });
      if (incomes.length > 4) addText(`+${incomes.length - 4} more`, margin + 3, incY, { fontSize: 7, color: [150, 150, 150] });
      
      // Expenses column
      pdf.setFillColor(239, 68, 68, 30);
      pdf.roundedRect(margin + colWidth + 5, yPos, colWidth, 35, 2, 2, 'F');
      addText('Top Expenses', margin + colWidth + 8, yPos + 6, { fontSize: 9, fontStyle: 'bold', color: [200, 68, 68] });
      let expY = yPos + 12;
      const sortedExp = [...expenses].sort((a, b) => b.amount - a.amount).slice(0, 4);
      sortedExp.forEach((exp) => {
        const catName = EXPENSE_CATEGORIES[exp.category]?.name || exp.category;
        addText(`• ${catName}`, margin + colWidth + 8, expY, { fontSize: 8, color: [60, 60, 60] });
        addText(formatCurrency(exp.amount), pageWidth - margin - 3, expY, { fontSize: 8, fontStyle: 'bold', color: [200, 68, 68], align: 'right' });
        expY += 5;
      });
      if (expenses.length > 4) addText(`+${expenses.length - 4} more`, margin + colWidth + 8, expY, { fontSize: 7, color: [150, 150, 150] });
      
      yPos += 42;

      // ==================== EMI & EMERGENCY FUND ====================
      checkPageBreak(35);
      addSectionHeader('EMI Stress & Emergency Fund', '🏦');
      
      // EMI Analysis
      addText('EMI Analysis:', margin + 2, yPos, { fontSize: 9, fontStyle: 'bold', color: [60, 60, 60] });
      yPos += 6;
      const emiAmount = expenses.filter(e => e.is_emi).reduce((sum, e) => sum + e.amount, 0);
      addDataRow('Total EMI Outflow', formatCurrency(emiAmount), { valueColor: [239, 68, 68], bold: true });
      addDataRow('EMI-to-Income Ratio', `${emiRatio.toFixed(1)}%`, { valueColor: emiRatio < 30 ? [34, 197, 94] : emiRatio < 50 ? [251, 191, 36] : [239, 68, 68], bold: true });
      addDataRow('Status', emiRatio < 30 ? '✓ Safe Zone' : emiRatio < 50 ? '⚠ Moderate' : '✗ Danger Zone', { valueColor: emiRatio < 30 ? [34, 197, 94] : emiRatio < 50 ? [251, 191, 36] : [239, 68, 68], bold: true });
      yPos += 3;
      
      // Emergency Fund
      addText('Emergency Fund:', margin + 2, yPos, { fontSize: 9, fontStyle: 'bold', color: [60, 60, 60] });
      yPos += 6;
      const emergencyCurrent = budgetSummary?.emergency_fund_current || 0;
      const emergencyTarget = budgetSummary?.emergency_fund_target || (totalExpenses * 6);
      const emergencyCoverage = budgetSummary?.emergency_fund_coverage || 0;
      addDataRow('Current Fund', formatCurrency(emergencyCurrent), { valueColor: [59, 130, 246], bold: true });
      addDataRow('Target (6 months)', formatCurrency(emergencyTarget), { valueColor: [100, 100, 100] });
      addDataRow('Coverage', `${emergencyCoverage.toFixed(1)} months`, { valueColor: emergencyCoverage >= 6 ? [34, 197, 94] : emergencyCoverage >= 3 ? [251, 191, 36] : [239, 68, 68], bold: true });
      yPos += 5;

      // ==================== CASH LEAKS ====================
      if (cashLeaks?.leaks?.length > 0) {
        checkPageBreak(45);
        addSectionHeader('Cash Leak Detector - Savings Opportunities', '🔍');
        
        pdf.setFillColor(254, 243, 199);
        pdf.roundedRect(margin, yPos, contentWidth, 10, 2, 2, 'F');
        addText(`💡 Potential Annual Savings: ${formatCurrency((cashLeaks.total_potential_savings || 0) * 12)}`, margin + 3, yPos + 6.5, { fontSize: 10, fontStyle: 'bold', color: [180, 100, 0] });
        yPos += 14;
        
        cashLeaks.leaks.slice(0, 5).forEach((leak) => {
          const sevColor = leak.severity === 'high' ? [239, 68, 68] : [251, 191, 36];
          addText(`${leak.severity === 'high' ? '🔴' : '🟡'} ${leak.category}`, margin + 2, yPos, { fontSize: 9, fontStyle: 'bold', color: sevColor });
          addText(`${formatCurrency(leak.current_spend)}/mo`, pageWidth - margin - 2, yPos, { fontSize: 8, color: [100, 100, 100], align: 'right' });
          yPos += 5;
          if (leak.suggestion) {
            addText(`   → ${leak.suggestion}`, margin + 5, yPos, { fontSize: 8, color: [100, 100, 100], maxWidth: contentWidth - 10 });
            yPos += 5;
          }
        });
        yPos += 3;
      }

      // ==================== PAGE 2: INSIGHTS & RECOMMENDATIONS ====================
      checkPageBreak(60);
      
      // Behavioural Insights
      if (behaviouralInsights?.alerts?.length > 0 || behaviouralInsights?.insights?.length > 0) {
        addSectionHeader('Behavioural Insights & Alerts', '🧠');
        
        // Alerts
        if (behaviouralInsights.alerts?.length > 0) {
          addText('⚠️ Alerts Requiring Attention:', margin + 2, yPos, { fontSize: 9, fontStyle: 'bold', color: [239, 68, 68] });
          yPos += 6;
          behaviouralInsights.alerts.slice(0, 3).forEach((alert) => {
            const alertColor = alert.severity === 'high' ? [239, 68, 68] : [251, 191, 36];
            addText(`• ${alert.title}`, margin + 4, yPos, { fontSize: 8, fontStyle: 'bold', color: alertColor });
            yPos += 4;
            addText(alert.message, margin + 6, yPos, { fontSize: 7, color: [100, 100, 100], maxWidth: contentWidth - 10 });
            yPos += 6;
          });
        }
        
        // Positive insights
        if (behaviouralInsights.insights?.length > 0) {
          addText('✅ Positive Habits:', margin + 2, yPos, { fontSize: 9, fontStyle: 'bold', color: [34, 197, 94] });
          yPos += 6;
          behaviouralInsights.insights.slice(0, 3).forEach((insight) => {
            addText(`• ${insight.title}: ${insight.message}`, margin + 4, yPos, { fontSize: 8, color: [34, 150, 94], maxWidth: contentWidth - 8 });
            yPos += 5;
          });
        }
        yPos += 5;
      }

      // ==================== TAX AWARENESS ====================
      if (taxData) {
        checkPageBreak(50);
        addSectionHeader(`Tax Awareness - FY ${getCurrentFinancialYear()}`, '📋');
        
        // Tax summary boxes
        const taxItems = [
          { label: 'Total Deductions', value: formatCurrency(taxData.total_deductions || 0), color: [59, 130, 246] },
          { label: 'Tax Saved', value: formatCurrency(taxData.estimated_tax_saved || 0), color: [34, 197, 94] },
          { label: 'Optimization Potential', value: formatCurrency(taxData.summary?.optimization_potential || 0), color: [251, 191, 36] }
        ];
        
        const taxBoxWidth = (contentWidth - 6) / 3;
        taxItems.forEach((item, i) => {
          const x = margin + (i * (taxBoxWidth + 3));
          pdf.setFillColor(240, 240, 245);
          pdf.roundedRect(x, yPos, taxBoxWidth, 14, 2, 2, 'F');
          addText(item.label, x + 3, yPos + 5, { fontSize: 7, color: [100, 100, 100] });
          addText(item.value, x + 3, yPos + 10, { fontSize: 9, fontStyle: 'bold', color: item.color });
        });
        yPos += 18;
        
        // Section-wise breakdown
        addText('Section-wise Utilization:', margin + 2, yPos, { fontSize: 9, fontStyle: 'bold', color: [60, 60, 60] });
        yPos += 6;
        
        const sections = [
          { name: '80C (Investments)', claimed: taxData.section_80c?.total_claimed || 0, limit: 150000 },
          { name: '80D (Health Insurance)', claimed: taxData.section_80d?.total_claimed || 0, limit: 75000 },
          { name: '24 (Home Loan Interest)', claimed: taxData.section_24?.claimed || 0, limit: 200000 },
          { name: '80CCD (NPS)', claimed: taxData.section_80ccd?.claimed || 0, limit: 50000 }
        ];
        
        sections.forEach((sec) => {
          const utilization = sec.limit > 0 ? Math.round((sec.claimed / sec.limit) * 100) : 0;
          addText(sec.name, margin + 4, yPos, { fontSize: 8, color: [60, 60, 60] });
          addText(`${formatCurrency(sec.claimed)} / ${formatCurrency(sec.limit)} (${utilization}%)`, pageWidth - margin - 2, yPos, { fontSize: 8, color: utilization >= 100 ? [34, 197, 94] : [100, 100, 100], align: 'right' });
          yPos += 5;
        });
        yPos += 5;
      }

      // ==================== ACTION PLAN ====================
      checkPageBreak(60);
      addSectionHeader('Personalized Action Plan', '🎯');
      
      const actionItems = [];
      
      // Generate action items based on scores and data
      if (savingsRate < 20) {
        actionItems.push({ priority: 'HIGH', action: `Increase savings rate from ${savingsRate.toFixed(1)}% to at least 20%`, impact: 'Build wealth faster' });
      }
      if (emiRatio > 40) {
        actionItems.push({ priority: 'HIGH', action: `Reduce EMI burden from ${emiRatio.toFixed(1)}% to below 40%`, impact: 'Improve cash flow' });
      }
      if ((budgetSummary?.emergency_fund_coverage || 0) < 6) {
        const needed = (budgetSummary?.emergency_fund_target || 0) - (budgetSummary?.emergency_fund_current || 0);
        actionItems.push({ priority: 'HIGH', action: `Build emergency fund - need ${formatCurrency(needed)} more`, impact: 'Financial security' });
      }
      if ((healthScore?.insurance_score || 0) < 50) {
        actionItems.push({ priority: 'MEDIUM', action: 'Get adequate life and health insurance coverage', impact: 'Family protection' });
      }
      if ((budgetSummary?.investments_actual || 0) < budgetAllocation.investments) {
        const gap = budgetAllocation.investments - (budgetSummary?.investments_actual || 0);
        actionItems.push({ priority: 'MEDIUM', action: `Increase investments by ${formatCurrency(gap)}/month`, impact: 'Wealth creation' });
      }
      if (taxData?.summary?.optimization_potential > 50000) {
        actionItems.push({ priority: 'MEDIUM', action: `Save ${formatCurrency(taxData.summary.optimization_potential * 0.3)} more in taxes`, impact: 'Tax efficiency' });
      }
      if (cashLeaks?.leaks?.length > 0) {
        actionItems.push({ priority: 'LOW', action: `Review ${cashLeaks.leaks.length} cash leak areas to save ${formatCurrency(cashLeaks.total_potential_savings * 12)}/year`, impact: 'Plug money leaks' });
      }
      
      // Add improvements from health score
      if (healthScore?.improvements) {
        healthScore.improvements.slice(0, 3).forEach(imp => {
          actionItems.push({ priority: 'MEDIUM', action: imp, impact: 'Improve score' });
        });
      }
      
      // Display action items
      if (actionItems.length > 0) {
        actionItems.slice(0, 8).forEach((item, i) => {
          const priorityColor = item.priority === 'HIGH' ? [239, 68, 68] : item.priority === 'MEDIUM' ? [251, 191, 36] : [59, 130, 246];
          const priorityBg = item.priority === 'HIGH' ? [254, 226, 226] : item.priority === 'MEDIUM' ? [254, 249, 195] : [219, 234, 254];
          
          pdf.setFillColor(...priorityBg);
          pdf.roundedRect(margin, yPos, contentWidth, 10, 1, 1, 'F');
          
          addText(`[${item.priority}]`, margin + 2, yPos + 6, { fontSize: 7, fontStyle: 'bold', color: priorityColor });
          addText(item.action, margin + 20, yPos + 6, { fontSize: 8, color: [60, 60, 60], maxWidth: contentWidth - 60 });
          addText(item.impact, pageWidth - margin - 2, yPos + 6, { fontSize: 7, color: [100, 100, 100], align: 'right' });
          yPos += 12;
          
          if (yPos > pageHeight - 30) {
            checkPageBreak(15);
          }
        });
      } else {
        addText('🎉 Great job! Your finances are well-managed. Keep it up!', margin + 2, yPos, { fontSize: 10, color: [34, 197, 94] });
        yPos += 8;
      }

      // ==================== FINAL SUMMARY ====================
      checkPageBreak(35);
      yPos += 5;
      pdf.setFillColor(26, 41, 66);
      pdf.roundedRect(margin, yPos, contentWidth, 25, 3, 3, 'F');
      
      addText('📌 Key Takeaways', margin + 5, yPos + 8, { fontSize: 11, fontStyle: 'bold', color: [255, 215, 0] });
      
      const takeaways = [
        `Money Fitness Score: ${Math.round(score)}/100 (${rating})`,
        `Monthly Savings: ${formatCurrency(netSavings)} (${savingsRate.toFixed(1)}% of income)`,
        `${actionItems.filter(a => a.priority === 'HIGH').length} high-priority actions needed`
      ];
      
      takeaways.forEach((t, i) => {
        addText(`• ${t}`, margin + 5 + (i * 60), yPos + 18, { fontSize: 8, color: [200, 200, 200] });
      });
      
      // Add footer to last page
      addFooter();

      // ==================== SAVE PDF ====================
      const clientName = isAdvisorOrManager && selectedClient ? `_${selectedClient.name.replace(/\s+/g, '_')}` : '';
      const fileName = `Money_Meter_Report${clientName}_${currentMonth}.pdf`;
      pdf.save(fileName);
      
    } catch (error) {
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const addIncome = () => {
    if (!newIncome.amount || parseFloat(newIncome.amount) <= 0) return;
    
    setIncomes([...incomes, {
      ...newIncome,
      amount: parseFloat(newIncome.amount)
    }]);
    setNewIncome({ type: 'salary', amount: '', description: '', is_regular: true });
    setShowAddIncome(false);
  };

  const addExpense = () => {
    if (!newExpense.amount || parseFloat(newExpense.amount) <= 0) return;
    
    setExpenses([...expenses, {
      ...newExpense,
      amount: parseFloat(newExpense.amount)
    }]);
    setNewExpense({ category: 'household', subcategory: 'rent', amount: '', description: '', is_emi: false });
    setShowAddExpense(false);
  };

  const removeIncome = (index) => {
    setIncomes(incomes.filter((_, i) => i !== index));
  };

  const removeExpense = (index) => {
    setExpenses(expenses.filter((_, i) => i !== index));
  };

  const navigateMonth = (direction) => {
    const [year, month] = currentMonth.split('-').map(Number);
    const date = new Date(year, month - 1 + direction, 1);
    setCurrentMonth(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  const totalIncome = incomes.reduce((sum, i) => sum + i.amount, 0);
  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);
  const netSavings = totalIncome - totalExpenses;
  const savingsRate = totalIncome > 0 ? (netSavings / totalIncome * 100) : 0;

  // EMI Calculation
  const emiSubcategories = ['rent', 'vehicle_emi', 'personal_loan'];
  const totalEMI = expenses.filter(e => e.is_emi || emiSubcategories.includes(e.subcategory)).reduce((sum, e) => sum + e.amount, 0);
  const emiRatio = totalIncome > 0 ? (totalEMI / totalIncome * 100) : 0;

  // Get EMI Status
  const getEMIStatus = () => {
    if (emiRatio < 30) return { status: 'Safe', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (emiRatio < 50) return { status: 'Moderate', color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    return { status: 'Risky', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  const emiStatus = getEMIStatus();

  // Budget Allocation (India-Optimized 50-20-20-10)
  const budgetAllocation = {
    essentials: totalIncome * 0.50,
    lifestyle: totalIncome * 0.20,
    investments: totalIncome * 0.20,
    protection: totalIncome * 0.10
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 65) return 'text-blue-400';
    if (score >= 50) return 'text-yellow-400';
    if (score >= 35) return 'text-orange-400';
    return 'text-red-400';
  };

  const getRatingBadge = (rating) => {
    const badges = {
      'Excellent': 'bg-green-500/20 text-green-400 border-green-500/30',
      'Good': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'Average': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'Needs Attention': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'Critical': 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return badges[rating] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  if (loading && !isAdvisorOrManager) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <FaSpinner className="animate-spin text-4xl text-[#FFD700]" />
      </div>
    );
  }

  // Client selector view for advisors/managers when no client is selected
  if (isAdvisorOrManager && !selectedClientId) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <FaCalculator className="text-[#FFD700]" />
            Money Meter
          </h1>
          <p className="text-gray-400 mt-1">View your clients' monthly budget and financial health</p>
        </div>

        {/* Client Selection */}
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
            <FaUsers className="text-[#FFD700]" />
            Select a Client to View Their Money Meter
          </h3>
          
          {clients.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <FaUserCircle className="text-5xl mx-auto mb-3 opacity-50" />
              <p>No clients found. Add clients to view their budget data.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.map((client) => (
                <button
                  key={client.id || client.user_id}
                  onClick={() => {
                    setSelectedClientId(client.user_id || client.id);
                    setSelectedClient(client);
                  }}
                  className="bg-[#0F172A] hover:bg-[#1A2942] rounded-xl p-4 text-left border border-[#2D5A8F] hover:border-[#FFD700] transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#FFD700] to-[#FFA500] rounded-full flex items-center justify-center text-[#0F172A] font-bold text-lg">
                      {client.name?.charAt(0) || 'C'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium truncate">{client.name || 'Unnamed Client'}</div>
                      <div className="text-gray-400 text-sm truncate">{client.email || client.contact}</div>
                      {user?.role === 'manager' && client.advisor_name && (
                        <div className="text-xs text-[#FFD700] mt-1">Advisor: {client.advisor_name}</div>
                      )}
                    </div>
                    <FaEye className="text-gray-500 group-hover:text-[#FFD700] transition-colors" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show loading when fetching client data
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <FaSpinner className="animate-spin text-4xl text-[#FFD700]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Client Info Banner for Advisors/Managers */}
      {isAdvisorOrManager && selectedClient && (
        <div className="bg-gradient-to-r from-[#FFD700]/20 to-[#FFA500]/10 rounded-xl p-4 border border-[#FFD700]/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FFD700] rounded-full flex items-center justify-center text-[#0F172A] font-bold">
              {selectedClient.name?.charAt(0) || 'C'}
            </div>
            <div>
              <div className="text-white font-medium">Viewing: {selectedClient.name || 'Client'}</div>
              <div className="text-gray-400 text-sm">{selectedClient.email || selectedClient.contact}</div>
            </div>
          </div>
          <button
            onClick={() => {
              setSelectedClientId(null);
              setSelectedClient(null);
              setIncomes([]);
              setExpenses([]);
              setHealthScore(null);
              setBudgetSummary(null);
            }}
            className="px-4 py-2 bg-[#1A2942] hover:bg-[#2D5A8F] text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <FaUsers />
            Change Client
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <FaCalculator className="text-[#FFD700]" />
            Money Meter
          </h1>
          <p className="text-gray-400 mt-1">{isAdvisorOrManager ? "Client's Monthly Budget Planner" : "Your Monthly Budget Planner"}</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* PDF Download Button */}
          <button
            onClick={generatePDF}
            disabled={generatingPDF || !healthScore}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
              generatingPDF || !healthScore
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#0F172A] hover:shadow-lg hover:shadow-[#FFD700]/30'
            }`}
            data-testid="download-pdf-btn"
          >
            {generatingPDF ? (
              <>
                <FaSpinner className="animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <FaFilePdf />
                Download Report
              </>
            )}
          </button>
          
          {/* Month Navigator */}
          <div className="flex items-center gap-3 bg-[#1A2942] rounded-xl p-2">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-2 hover:bg-[#2D5A8F] rounded-lg transition-colors"
            >
              <FaChevronLeft className="text-gray-400" />
            </button>
            <span className="text-white font-medium px-4 min-w-[160px] text-center">
              {formatMonth(currentMonth)}
            </span>
            <button
              onClick={() => navigateMonth(1)}
              className="p-2 hover:bg-[#2D5A8F] rounded-lg transition-colors"
            >
              <FaChevronRight className="text-gray-400" />
            </button>
          </div>
        </div>
      </div>

      {/* Budget Health Score Card */}
      {healthScore && healthScore.overall_score > 0 && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F] overflow-visible">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 overflow-visible">
            <div className="flex items-center gap-4 overflow-visible">
              <div className="w-20 h-20 rounded-full bg-[#0F172A] flex items-center justify-center border-4 border-[#FFD700] relative group cursor-help">
                <span className={`text-3xl font-bold ${getScoreColor(healthScore.overall_score)}`}>
                  {Math.round(healthScore.overall_score)}
                </span>
                {/* Overall Score Tooltip - Positioned to the right */}
                <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 w-64 p-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-[100]">
                  <div className="text-sm text-white font-medium mb-2">Money Fitness Rating: {Math.round(healthScore.overall_score)}/100</div>
                  <div className="text-xs text-gray-300 space-y-1">
                    <div className="flex justify-between"><span className="text-green-400">80-100:</span> Excellent Financial Health</div>
                    <div className="flex justify-between"><span className="text-blue-400">60-79:</span> Good, Minor Improvements</div>
                    <div className="flex justify-between"><span className="text-yellow-400">40-59:</span> Needs Attention</div>
                    <div className="flex justify-between"><span className="text-red-400">0-39:</span> Critical - Act Now</div>
                  </div>
                  {/* Arrow pointing left */}
                  <div className="absolute top-1/2 -left-2 -translate-y-1/2 w-3 h-3 bg-[#0F172A] border-l border-b border-[#2D5A8F] rotate-45"></div>
                </div>
              </div>
              <div>
                <h3 className="text-white font-bold text-lg flex items-center gap-2">
                  <FaTrophy className="text-[#FFD700]" />
                  Money Fitness Rating
                </h3>
                <span className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-medium border ${getRatingBadge(healthScore.rating)}`}>
                  {healthScore.rating}
                </span>
                <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                  <FaInfoCircle />
                  Hover over scores for details
                </div>
              </div>
            </div>
            
            {/* Score Breakdown with Interpretations */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3 text-center">
              {[
                { 
                  label: 'Savings', 
                  score: healthScore.savings_score,
                  tooltip: 'Measures your savings rate. 70+ is excellent (>20% savings), 50-70 is good, below 50 needs improvement.',
                  getInterpretation: (s) => s >= 70 ? 'Excellent savings habit!' : s >= 50 ? 'Good, aim for 20%+ savings' : 'Low savings rate - cut expenses'
                },
                { 
                  label: 'EMI', 
                  score: healthScore.emi_score,
                  tooltip: 'EMI-to-income ratio. 80+ means EMIs are <30% of income (healthy), 50-80 is moderate, below 50 is risky.',
                  getInterpretation: (s) => s >= 80 ? 'EMIs well managed' : s >= 50 ? 'EMIs are moderate' : 'EMI burden too high (>40%)'
                },
                { 
                  label: 'Emergency', 
                  score: healthScore.emergency_score,
                  tooltip: 'Emergency fund coverage. 100 means 6+ months covered, 50 means 3 months, below 50 is insufficient.',
                  getInterpretation: (s) => s >= 80 ? '6+ months covered!' : s >= 50 ? '3-6 months covered' : 'Build emergency fund urgently'
                },
                { 
                  label: 'Investment', 
                  score: healthScore.investment_score,
                  tooltip: 'Investment allocation vs budget. 100 means you\'re meeting your investment goals, 50 is halfway there.',
                  getInterpretation: (s) => s >= 80 ? 'Investment goals on track' : s >= 50 ? 'Increase SIP/investments' : 'Start investing regularly'
                },
                { 
                  label: 'Insurance', 
                  score: healthScore.insurance_score,
                  tooltip: 'Insurance protection level. 100 means adequate coverage (10x income + health), 0 means no protection.',
                  getInterpretation: (s) => s >= 80 ? 'Well protected' : s >= 50 ? 'Increase coverage' : 'Get life & health insurance!'
                },
                { 
                  label: 'Lifestyle', 
                  score: healthScore.lifestyle_score,
                  tooltip: 'Lifestyle spending control. 100 means within budget, lower scores indicate overspending on non-essentials.',
                  getInterpretation: (s) => s >= 80 ? 'Spending under control' : s >= 50 ? 'Watch discretionary spending' : 'Lifestyle inflation alert!'
                }
              ].map((item, idx) => (
                <div key={idx} className="bg-[#0F172A] rounded-lg p-2 group relative cursor-help overflow-visible">
                  <div className={`text-lg font-bold ${getScoreColor(item.score)}`}>
                    {Math.round(item.score)}
                  </div>
                  <div className="text-xs text-gray-400">{item.label}</div>
                  {/* Tooltip - Positioned below */}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 p-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-[100]">
                    <div className="text-xs text-gray-300 mb-2">{item.tooltip}</div>
                    <div className={`text-xs font-medium ${getScoreColor(item.score)}`}>
                      {item.getInterpretation(item.score)}
                    </div>
                    {/* Arrow pointing up */}
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-3 h-3 bg-[#0F172A] border-l border-t border-[#2D5A8F] rotate-45"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Insights */}
          {(healthScore.strengths?.length > 0 || healthScore.improvements?.length > 0) && (
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              {healthScore.strengths?.length > 0 && (
                <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/20">
                  <h4 className="text-green-400 font-medium text-sm mb-2">Strengths</h4>
                  {healthScore.strengths.map((s, i) => (
                    <p key={i} className="text-gray-300 text-sm flex items-start gap-2">
                      <FaCheckCircle className="text-green-400 mt-1 flex-shrink-0" />
                      {s}
                    </p>
                  ))}
                </div>
              )}
              {healthScore.improvements?.length > 0 && (
                <div className="bg-orange-500/10 rounded-lg p-3 border border-orange-500/20">
                  <h4 className="text-orange-400 font-medium text-sm mb-2">Areas to Improve</h4>
                  {healthScore.improvements.map((s, i) => (
                    <p key={i} className="text-gray-300 text-sm flex items-start gap-2">
                      <FaInfoCircle className="text-orange-400 mt-1 flex-shrink-0" />
                      {s}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-xl p-4 border border-green-500/30">
          <div className="flex items-center gap-2 text-green-400 mb-2">
            <FaArrowUp />
            <span className="text-sm">Total Income</span>
          </div>
          <div className="text-2xl font-bold text-white">₹{totalIncome.toLocaleString('en-IN')}</div>
        </div>
        
        <div className="bg-gradient-to-br from-red-500/20 to-red-600/10 rounded-xl p-4 border border-red-500/30">
          <div className="flex items-center gap-2 text-red-400 mb-2">
            <FaArrowDown />
            <span className="text-sm">Total Expenses</span>
          </div>
          <div className="text-2xl font-bold text-white">₹{totalExpenses.toLocaleString('en-IN')}</div>
        </div>
        
        <div className={`bg-gradient-to-br ${netSavings >= 0 ? 'from-blue-500/20 to-blue-600/10 border-blue-500/30' : 'from-orange-500/20 to-orange-600/10 border-orange-500/30'} rounded-xl p-4 border`}>
          <div className={`flex items-center gap-2 ${netSavings >= 0 ? 'text-blue-400' : 'text-orange-400'} mb-2`}>
            <FaWallet />
            <span className="text-sm">Net Savings</span>
          </div>
          <div className="text-2xl font-bold text-white">₹{netSavings.toLocaleString('en-IN')}</div>
          <div className={`text-sm ${netSavings >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
            {savingsRate.toFixed(1)}% of income
          </div>
        </div>
        
        <div className={`bg-gradient-to-br ${emiStatus.bg} rounded-xl p-4 border border-[#2D5A8F]`}>
          <div className={`flex items-center gap-2 ${emiStatus.color} mb-2`}>
            <FaCreditCard />
            <span className="text-sm">EMI Stress</span>
          </div>
          <div className="text-2xl font-bold text-white">{emiRatio.toFixed(1)}%</div>
          <div className={`text-sm ${emiStatus.color}`}>{emiStatus.status}</div>
        </div>
      </div>

      {/* India-Optimized Budget Rule */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h3 className="text-white font-bold mb-2 flex items-center gap-2">
          <FaChartPie className="text-[#FFD700]" />
          India-Optimized Budget Rule (50-20-20-10)
        </h3>
        <p className="text-gray-400 text-sm mb-4">Compare your actual spending against the ideal budget allocation based on your income of ₹{totalIncome.toLocaleString('en-IN')}</p>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { 
              label: 'Essentials', 
              benchmarkPercent: 50, 
              benchmark: budgetAllocation.essentials, 
              actual: budgetSummary?.essentials_actual || 0,
              color: 'blue',
              icon: '🏠',
              description: 'Rent, groceries, utilities, transport'
            },
            { 
              label: 'Lifestyle', 
              benchmarkPercent: 20, 
              benchmark: budgetAllocation.lifestyle, 
              actual: budgetSummary?.lifestyle_actual || 0,
              color: 'purple',
              icon: '🎯',
              description: 'Entertainment, dining, shopping'
            },
            { 
              label: 'Investments', 
              benchmarkPercent: 20, 
              benchmark: budgetAllocation.investments, 
              actual: budgetSummary?.investments_actual || 0,
              color: 'green',
              icon: '📈',
              description: 'SIP, mutual funds, stocks'
            },
            { 
              label: 'Protection', 
              benchmarkPercent: 10, 
              benchmark: budgetAllocation.protection, 
              actual: budgetSummary?.protection_actual || 0,
              color: 'orange',
              icon: '🛡️',
              description: 'Insurance premiums, health cover'
            }
          ].map((item, idx) => {
            const actualPercent = totalIncome > 0 ? Math.round((item.actual / totalIncome) * 100) : 0;
            const diff = item.actual - item.benchmark;
            const isOver = diff > 0;
            const isUnder = diff < 0;
            const variance = Math.abs(diff);
            const variancePercent = item.benchmark > 0 ? Math.round((variance / item.benchmark) * 100) : 0;
            
            // Determine status
            let status, statusColor, statusBg;
            if (item.label === 'Investments' || item.label === 'Protection') {
              // For investments and protection, more is better
              if (actualPercent >= item.benchmarkPercent) {
                status = 'On Track';
                statusColor = 'text-green-400';
                statusBg = 'bg-green-500/20';
              } else if (actualPercent >= item.benchmarkPercent * 0.5) {
                status = 'Below Target';
                statusColor = 'text-yellow-400';
                statusBg = 'bg-yellow-500/20';
              } else {
                status = 'Action Needed';
                statusColor = 'text-red-400';
                statusBg = 'bg-red-500/20';
              }
            } else {
              // For essentials and lifestyle, less or equal is better
              if (actualPercent <= item.benchmarkPercent) {
                status = 'On Track';
                statusColor = 'text-green-400';
                statusBg = 'bg-green-500/20';
              } else if (actualPercent <= item.benchmarkPercent * 1.2) {
                status = 'Slightly Over';
                statusColor = 'text-yellow-400';
                statusBg = 'bg-yellow-500/20';
              } else {
                status = 'Over Budget';
                statusColor = 'text-red-400';
                statusBg = 'bg-red-500/20';
              }
            }
            
            const colorClasses = {
              blue: { bg: 'bg-blue-500', light: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
              purple: { bg: 'bg-purple-500', light: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
              green: { bg: 'bg-green-500', light: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
              orange: { bg: 'bg-orange-500', light: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' }
            }[item.color];
            
            return (
              <div key={idx} className={`bg-[#0F172A] rounded-xl p-4 border ${colorClasses.border}`}>
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{item.icon}</span>
                    <span className="text-white font-medium">{item.label}</span>
                  </div>
                  <span className={`${colorClasses.bg} text-white px-2 py-0.5 rounded text-xs font-bold`}>
                    {item.benchmarkPercent}%
                  </span>
                </div>
                
                {/* Description */}
                <p className="text-gray-500 text-xs mb-3">{item.description}</p>
                
                {/* Benchmark vs Actual */}
                <div className="space-y-2 mb-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-xs">Benchmark:</span>
                    <span className="text-gray-300 text-sm">₹{item.benchmark.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-xs">Actual:</span>
                    <span className={`font-bold ${colorClasses.text}`}>₹{item.actual.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-xs">Your %:</span>
                    <span className={`text-sm font-medium ${actualPercent > item.benchmarkPercent ? (item.label === 'Investments' || item.label === 'Protection' ? 'text-green-400' : 'text-red-400') : (item.label === 'Investments' || item.label === 'Protection' ? 'text-yellow-400' : 'text-green-400')}`}>
                      {actualPercent}%
                    </span>
                  </div>
                </div>
                
                {/* Progress Bar - Dual bar showing benchmark and actual */}
                <div className="relative h-3 bg-[#1A2942] rounded-full mb-3 overflow-hidden">
                  {/* Benchmark line */}
                  <div 
                    className="absolute h-full border-r-2 border-white/50 z-10"
                    style={{ width: `${Math.min(100, item.benchmarkPercent * 2)}%` }}
                  ></div>
                  {/* Actual bar */}
                  <div 
                    className={`h-full ${colorClasses.bg} rounded-full transition-all`}
                    style={{ width: `${Math.min(100, actualPercent * 2)}%` }}
                  ></div>
                </div>
                
                {/* Status Badge */}
                <div className={`flex items-center justify-between ${statusBg} rounded-lg px-3 py-2`}>
                  <span className={`text-xs font-medium ${statusColor}`}>{status}</span>
                  {variance > 0 && (
                    <span className={`text-xs ${statusColor}`}>
                      {isOver ? '+' : '-'}₹{variance.toLocaleString('en-IN')}
                    </span>
                  )}
                </div>
                
                {/* Action hint */}
                {status !== 'On Track' && (
                  <p className="text-gray-500 text-xs mt-2 italic">
                    {(item.label === 'Investments' || item.label === 'Protection') && isUnder 
                      ? `Increase by ₹${variance.toLocaleString('en-IN')} to meet target`
                      : isOver && (item.label === 'Essentials' || item.label === 'Lifestyle')
                      ? `Reduce by ₹${variance.toLocaleString('en-IN')} to stay on budget`
                      : ''}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#2D5A8F] pb-2">
        <button
          onClick={() => setActiveTab('income')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === 'income' 
              ? 'bg-green-500/20 text-green-400 border-b-2 border-green-500' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <FaArrowUp className="inline mr-2" />
          Income ({incomes.length})
        </button>
        <button
          onClick={() => setActiveTab('expenses')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === 'expenses' 
              ? 'bg-red-500/20 text-red-400 border-b-2 border-red-500' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <FaArrowDown className="inline mr-2" />
          Expenses ({expenses.length})
        </button>
        <button
          onClick={() => setActiveTab('emergency')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === 'emergency' 
              ? 'bg-blue-500/20 text-blue-400 border-b-2 border-blue-500' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <FaHeart className="inline mr-2" />
          Emergency Fund
        </button>
        <button
          onClick={() => setActiveTab('goals')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === 'goals' 
              ? 'bg-purple-500/20 text-purple-400 border-b-2 border-purple-500' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <FaBullseye className="inline mr-2" />
          Goal Planning
        </button>
        <button
          onClick={() => setActiveTab('leaks')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === 'leaks' 
              ? 'bg-orange-500/20 text-orange-400 border-b-2 border-orange-500' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <FaSearch className="inline mr-2" />
          Cash Leaks
        </button>
        <button
          onClick={() => setActiveTab('family')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === 'family' 
              ? 'bg-pink-500/20 text-pink-400 border-b-2 border-pink-500' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <FaUserFriends className="inline mr-2" />
          Family
        </button>
        <button
          onClick={() => setActiveTab('insights')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === 'insights' 
              ? 'bg-cyan-500/20 text-cyan-400 border-b-2 border-cyan-500' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <FaBell className="inline mr-2" />
          Insights
        </button>
        <button
          onClick={() => setActiveTab('tax')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === 'tax' 
              ? 'bg-teal-500/20 text-teal-400 border-b-2 border-teal-500' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <FaFileInvoiceDollar className="inline mr-2" />
          Tax
        </button>
      </div>

      {/* Income Tab */}
      {activeTab === 'income' && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-white font-bold">Income Sources</h3>
            <button
              onClick={() => setShowAddIncome(true)}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg flex items-center gap-2 transition-colors"
            >
              <FaPlus /> Add Income
            </button>
          </div>
          
          {showAddIncome && (
            <div className="bg-[#0F172A] rounded-lg p-4 mb-4 border border-green-500/30">
              <div className="grid md:grid-cols-4 gap-4">
                <select
                  value={newIncome.type}
                  onChange={(e) => setNewIncome({ ...newIncome, type: e.target.value })}
                  className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                >
                  {Object.entries(INCOME_TYPES).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                <input
                  type="number"
                  placeholder="Amount (₹)"
                  value={newIncome.amount}
                  onChange={(e) => setNewIncome({ ...newIncome, amount: e.target.value })}
                  className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                />
                <input
                  type="text"
                  placeholder="Description (optional)"
                  value={newIncome.description}
                  onChange={(e) => setNewIncome({ ...newIncome, description: e.target.value })}
                  className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                />
                <div className="flex gap-2">
                  <button
                    onClick={addIncome}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white rounded-lg px-4 py-2 transition-colors"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => setShowAddIncome(false)}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {incomes.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <FaWallet className="text-4xl mx-auto mb-3 opacity-50" />
              <p>No income entries yet. Add your income sources to get started.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {incomes.map((income, index) => (
                <div key={index} className="flex items-center justify-between bg-[#0F172A] rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                      <FaMoneyBillWave className="text-green-400" />
                    </div>
                    <div>
                      <div className="text-white font-medium">{INCOME_TYPES[income.type]}</div>
                      {income.description && (
                        <div className="text-gray-400 text-sm">{income.description}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-green-400 font-bold">₹{income.amount.toLocaleString('en-IN')}</span>
                    <button
                      onClick={() => removeIncome(index)}
                      className="text-red-400 hover:text-red-300 p-2"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Expenses Tab */}
      {activeTab === 'expenses' && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-white font-bold">Expense Entries</h3>
            <button
              onClick={() => setShowAddExpense(true)}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg flex items-center gap-2 transition-colors"
            >
              <FaPlus /> Add Expense
            </button>
          </div>
          
          {showAddExpense && (
            <div className="bg-[#0F172A] rounded-lg p-4 mb-4 border border-red-500/30">
              <div className="grid md:grid-cols-5 gap-4">
                <select
                  value={newExpense.category}
                  onChange={(e) => {
                    const cat = e.target.value;
                    const firstSub = Object.keys(EXPENSE_CATEGORIES[cat].subcategories)[0];
                    setNewExpense({ ...newExpense, category: cat, subcategory: firstSub });
                  }}
                  className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                >
                  {Object.entries(EXPENSE_CATEGORIES).map(([key, cat]) => (
                    <option key={key} value={key}>{cat.name}</option>
                  ))}
                </select>
                <select
                  value={newExpense.subcategory}
                  onChange={(e) => setNewExpense({ ...newExpense, subcategory: e.target.value })}
                  className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                >
                  {Object.entries(EXPENSE_CATEGORIES[newExpense.category].subcategories).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                <input
                  type="number"
                  placeholder="Amount (₹)"
                  value={newExpense.amount}
                  onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                  className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                />
                <label className="flex items-center gap-2 text-gray-300">
                  <input
                    type="checkbox"
                    checked={newExpense.is_emi}
                    onChange={(e) => setNewExpense({ ...newExpense, is_emi: e.target.checked })}
                    className="w-4 h-4"
                  />
                  EMI Payment
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={addExpense}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-lg px-4 py-2 transition-colors"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => setShowAddExpense(false)}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {expenses.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <FaCreditCard className="text-4xl mx-auto mb-3 opacity-50" />
              <p>No expense entries yet. Track your expenses to understand your spending.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {expenses.map((expense, index) => {
                const category = EXPENSE_CATEGORIES[expense.category];
                const CategoryIcon = category?.icon || FaCreditCard;
                return (
                  <div key={index} className="flex items-center justify-between bg-[#0F172A] rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 ${category?.color || 'bg-gray-500'}/20 rounded-lg flex items-center justify-center`}>
                        <CategoryIcon className={`${category?.color?.replace('bg-', 'text-') || 'text-gray-400'}`} />
                      </div>
                      <div>
                        <div className="text-white font-medium">
                          {category?.subcategories[expense.subcategory] || expense.subcategory}
                          {expense.is_emi && (
                            <span className="ml-2 text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded">EMI</span>
                          )}
                        </div>
                        <div className="text-gray-400 text-sm">{category?.name}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-red-400 font-bold">₹{expense.amount.toLocaleString('en-IN')}</span>
                      <button
                        onClick={() => removeExpense(index)}
                        className="text-red-400 hover:text-red-300 p-2"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Emergency Fund Tab */}
      {activeTab === 'emergency' && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <h3 className="text-white font-bold mb-4 flex items-center gap-2">
            <FaHeart className="text-red-400" />
            Emergency Fund Tracker
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-300 text-sm mb-2">Current Emergency Fund (₹)</label>
              <input
                type="number"
                value={emergencyFundCurrent}
                onChange={(e) => setEmergencyFundCurrent(parseFloat(e.target.value) || 0)}
                className="w-full bg-[#0F172A] border border-[#2D5A8F] rounded-lg px-4 py-3 text-white text-lg"
                placeholder="Enter your current emergency fund"
              />
              <p className="text-gray-400 text-sm mt-2">
                <FaInfoCircle className="inline mr-1" />
                Include savings that are easily accessible for emergencies
              </p>
            </div>
            
            <div className="bg-[#0F172A] rounded-lg p-4">
              <div className="text-gray-400 text-sm mb-2">Target (6 months of expenses)</div>
              <div className="text-2xl font-bold text-white mb-4">
                ₹{(totalExpenses * 6).toLocaleString('en-IN')}
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Current Coverage</span>
                  <span className="text-white font-medium">
                    {totalExpenses > 0 ? (emergencyFundCurrent / totalExpenses).toFixed(1) : 0} months
                  </span>
                </div>
                <div className="h-4 bg-[#1A2942] rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${
                      emergencyFundCurrent >= totalExpenses * 6 ? 'bg-green-500' :
                      emergencyFundCurrent >= totalExpenses * 3 ? 'bg-yellow-500' : 'bg-red-500'
                    } transition-all`}
                    style={{ width: `${Math.min(100, totalExpenses > 0 ? (emergencyFundCurrent / (totalExpenses * 6)) * 100 : 0)}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-400">
                  <span>0</span>
                  <span>3 months</span>
                  <span>6 months</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Goal-Linked Budgeting Tab */}
      {activeTab === 'goals' && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-white font-bold flex items-center gap-2">
                <FaBullseye className="text-purple-400" />
                Goal-Linked Budgeting
              </h3>
              <p className="text-gray-400 text-sm mt-1">
                Map your monthly savings to specific life goals. Track progress and identify funding gaps.
              </p>
            </div>
            {!isAdvisorOrManager && goalData?.goals && (
              <button
                onClick={saveGoalAllocations}
                disabled={savingGoals}
                className="px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {savingGoals ? <FaSpinner className="animate-spin" /> : <FaSave />}
                Save Allocations
              </button>
            )}
          </div>
          
          {goalData && goalData.goals ? (
            <>
              {/* Alerts Section */}
              {goalData.alerts && goalData.alerts.length > 0 && (
                <div className="mb-6 space-y-2">
                  {goalData.alerts.map((alert, idx) => (
                    <div key={idx} className={`flex items-center gap-3 p-3 rounded-lg ${
                      alert.severity === 'high' 
                        ? 'bg-red-500/20 border border-red-500/40' 
                        : 'bg-yellow-500/20 border border-yellow-500/40'
                    }`}>
                      <FaExclamationTriangle className={alert.severity === 'high' ? 'text-red-400' : 'text-yellow-400'} />
                      <span className={`text-sm font-medium ${alert.severity === 'high' ? 'text-red-300' : 'text-yellow-300'}`}>
                        {alert.message}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-[#0F172A] rounded-lg p-4">
                  <div className="text-gray-400 text-sm">Net Savings</div>
                  <div className="text-xl font-bold text-green-400">₹{goalData.net_savings?.toLocaleString('en-IN') || 0}</div>
                </div>
                <div className="bg-[#0F172A] rounded-lg p-4">
                  <div className="text-gray-400 text-sm">Recommended</div>
                  <div className="text-xl font-bold text-blue-400">₹{goalData.total_recommended_allocation?.toLocaleString('en-IN') || 0}</div>
                </div>
                <div className="bg-[#0F172A] rounded-lg p-4">
                  <div className="text-gray-400 text-sm">Your Allocation</div>
                  <div className="text-xl font-bold text-purple-400">₹{goalData.total_actual_allocation?.toLocaleString('en-IN') || 0}</div>
                </div>
                <div className="bg-[#0F172A] rounded-lg p-4">
                  <div className="text-gray-400 text-sm">Unallocated</div>
                  <div className={`text-xl font-bold ${goalData.surplus >= 0 ? 'text-green-400' : 'text-orange-400'}`}>
                    ₹{goalData.surplus?.toLocaleString('en-IN') || 0}
                  </div>
                </div>
              </div>

              {/* Goals List with Input Fields */}
              <div className="space-y-4">
                {goalData.goals.map((goal) => {
                  const iconMap = {
                    'education': FaGraduationCap,
                    'retirement': FaUmbrella,
                    'marriage': FaHeart,
                    'wealth': FaChartLine,
                    'emergency': FaShieldAlt,
                    'other': FaBullseye
                  };
                  const IconComponent = iconMap[goal.icon] || FaBullseye;
                  
                  const colorConfig = {
                    'blue': { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
                    'green': { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
                    'pink': { bg: 'bg-pink-500/20', text: 'text-pink-400', border: 'border-pink-500/30' },
                    'purple': { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
                    'orange': { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' },
                    'gray': { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30' }
                  };
                  const colors = colorConfig[goal.color] || colorConfig.gray;
                  
                  const statusConfig = {
                    'on_track': { text: 'text-green-400', label: 'On Track' },
                    'warning': { text: 'text-yellow-400', label: 'Needs Attention' },
                    'critical': { text: 'text-red-400', label: 'Critical' }
                  };
                  const status = statusConfig[goal.status] || statusConfig.warning;
                  
                  const progress = goal.recommended_monthly > 0 
                    ? Math.min(100, (goalAllocations[goal.id] || goal.actual_monthly || 0) / goal.recommended_monthly * 100)
                    : 0;
                  
                  return (
                    <div key={goal.id} className={`bg-[#0F172A] rounded-xl p-4 border ${colors.border}`}>
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        {/* Goal Info */}
                        <div className="flex items-center gap-4 min-w-[250px]">
                          <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                            <IconComponent className={colors.text} size={24} />
                          </div>
                          <div>
                            <h4 className="text-white font-bold">{goal.name}</h4>
                            <p className="text-gray-400 text-xs">{goal.description}</p>
                            <span className={`text-xs font-medium ${status.text}`}>{status.label}</span>
                          </div>
                        </div>
                        
                        {/* Metrics */}
                        <div className="flex flex-wrap items-center gap-4 lg:gap-6">
                          {/* Required Amount */}
                          <div className="text-center min-w-[90px]">
                            <div className="text-gray-400 text-xs mb-1">Required/month</div>
                            <div className="text-white font-bold text-sm">₹{goal.recommended_monthly?.toLocaleString('en-IN')}</div>
                            <div className="text-gray-500 text-xs">({goal.recommended_percentage}% of savings)</div>
                          </div>
                          
                          {/* Allocation Input */}
                          <div className="text-center min-w-[120px]">
                            <div className="text-gray-400 text-xs mb-1">Your Allocation</div>
                            {isAdvisorOrManager ? (
                              <div className={`font-bold ${status.text}`}>₹{goal.actual_monthly?.toLocaleString('en-IN') || 0}</div>
                            ) : (
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">₹</span>
                                <input
                                  type="number"
                                  value={goalAllocations[goal.id] || ''}
                                  onChange={(e) => setGoalAllocations({...goalAllocations, [goal.id]: parseFloat(e.target.value) || 0})}
                                  placeholder="0"
                                  className="w-24 pl-6 pr-2 py-1.5 bg-[#1A2942] border border-[#2D5A8F] rounded-lg text-white text-sm focus:border-[#FFD700] focus:outline-none text-center"
                                />
                              </div>
                            )}
                          </div>
                          
                          {/* Gap Indicator */}
                          <div className="text-center min-w-[80px]">
                            <div className="text-gray-400 text-xs mb-1">Gap</div>
                            <div className={`font-bold text-sm ${goal.gap > 0 ? 'text-red-400' : 'text-green-400'}`}>
                              {goal.gap > 0 ? '-' : '+'}₹{Math.abs(goal.gap)?.toLocaleString('en-IN')}
                            </div>
                          </div>
                          
                          {/* Progress Bar */}
                          <div className="text-center min-w-[100px]">
                            <div className="text-gray-400 text-xs mb-1">Progress</div>
                            <div className="h-2 bg-[#1A2942] rounded-full w-24">
                              <div 
                                className={`h-full rounded-full transition-all ${
                                  progress >= 80 ? 'bg-green-500' : progress >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                            <div className="text-xs text-gray-400 mt-1">{progress.toFixed(0)}%</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Total Allocation Summary */}
              <div className="mt-6 p-4 bg-[#0F172A] rounded-xl border border-[#2D5A8F]">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className="text-gray-400 text-sm">Total Allocated vs Net Savings</div>
                    <div className="text-lg font-bold text-white">
                      ₹{Object.values(goalAllocations).reduce((a, b) => a + (parseFloat(b) || 0), 0).toLocaleString('en-IN')} / ₹{goalData.net_savings?.toLocaleString('en-IN')}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-gray-400 text-sm">Allocation Rate</div>
                    <div className={`text-lg font-bold ${goalData.allocation_percentage >= 80 ? 'text-green-400' : goalData.allocation_percentage >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {goalData.allocation_percentage?.toFixed(1) || 0}%
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <FaBullseye className="text-4xl mx-auto mb-3 opacity-50" />
              <p>Save your budget first to start goal-linked planning</p>
              <p className="text-sm mt-2">Add your income and expenses, then click "Save Budget"</p>
            </div>
          )}
        </div>
      )}

      {/* Cash Leak Detector Tab */}
      {activeTab === 'leaks' && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <h3 className="text-white font-bold mb-2 flex items-center gap-2">
            <FaSearch className="text-orange-400" />
            Cash Leak Detector
          </h3>
          <p className="text-gray-400 text-sm mb-6">
            Automatic analysis of your spending patterns to identify financial drains and improve discipline.
          </p>
          
          {cashLeaks && !cashLeaks.message ? (
            <>
              {/* Summary Header */}
              <div className={`rounded-xl p-4 mb-6 ${
                cashLeaks.leak_count === 0 
                  ? 'bg-green-500/10 border border-green-500/20' 
                  : cashLeaks.health_impact === 'High' 
                    ? 'bg-red-500/10 border border-red-500/20'
                    : 'bg-orange-500/10 border border-orange-500/20'
              }`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h4 className={`font-bold text-lg ${
                      cashLeaks.leak_count === 0 ? 'text-green-400' : 
                      cashLeaks.health_impact === 'High' ? 'text-red-400' : 'text-orange-400'
                    }`}>
                      {cashLeaks.leak_count === 0 
                        ? '🎉 Excellent! No Cash Leaks Detected' 
                        : `⚠️ ${cashLeaks.leak_count} Cash Leak(s) Found`}
                    </h4>
                    <p className="text-gray-300 text-sm mt-1">
                      {cashLeaks.leak_count === 0 
                        ? 'Your spending habits are well-optimized. Keep up the great work!'
                        : `Financial health impact: ${cashLeaks.health_impact}. Review suggestions below to plug the leaks.`}
                    </p>
                  </div>
                  {cashLeaks.total_potential_savings > 0 && (
                    <div className="text-center bg-[#0F172A] rounded-lg p-4 min-w-[150px]">
                      <div className="text-gray-400 text-xs">Potential Yearly Savings</div>
                      <div className="text-2xl font-bold text-green-400">
                        ₹{(cashLeaks.total_potential_savings * 12).toLocaleString('en-IN')}
                      </div>
                      <div className="text-gray-500 text-xs">₹{cashLeaks.total_potential_savings?.toLocaleString('en-IN')}/month</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Leaks List */}
              {cashLeaks.leaks && cashLeaks.leaks.length > 0 ? (
                <div className="space-y-4 mb-6">
                  <h4 className="text-white font-bold flex items-center gap-2">
                    <FaExclamationTriangle className="text-orange-400" />
                    Detected Leaks
                  </h4>
                  {cashLeaks.leaks.map((leak, index) => {
                    const iconMap = {
                      'subscription': FaTv,
                      'shopping': FaShoppingCart,
                      'debt': FaCreditCard,
                      'trending_up': FaArrowUp,
                      'food': FaUtensils
                    };
                    const IconComponent = iconMap[leak.icon] || FaExclamationTriangle;
                    
                    const severityColors = {
                      'high': { bg: 'bg-red-500/20', border: 'border-red-500/30', text: 'text-red-400' },
                      'medium': { bg: 'bg-orange-500/20', border: 'border-orange-500/30', text: 'text-orange-400' },
                      'low': { bg: 'bg-yellow-500/20', border: 'border-yellow-500/30', text: 'text-yellow-400' }
                    };
                    const colors = severityColors[leak.severity] || severityColors.medium;
                    
                    return (
                      <div key={index} className={`rounded-xl p-4 border ${colors.bg} ${colors.border}`}>
                        <div className="flex flex-col lg:flex-row gap-4">
                          {/* Leak Info */}
                          <div className="flex items-start gap-4 flex-1">
                            <div className={`w-14 h-14 rounded-xl ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                              <IconComponent className={colors.text} size={28} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="text-white font-bold text-lg">{leak.type}</h4>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${colors.bg} ${colors.text} border ${colors.border}`}>
                                  {leak.severity?.toUpperCase()}
                                </span>
                              </div>
                              <p className={`${colors.text} font-medium mb-2`}>{leak.impact}</p>
                              
                              {/* Details */}
                              {leak.details && leak.details.length > 0 && (
                                <div className="bg-[#0F172A]/50 rounded-lg p-3 mt-2">
                                  <div className="text-gray-400 text-xs mb-2">Top items:</div>
                                  <div className="space-y-1">
                                    {leak.details.slice(0, 3).map((item, idx) => (
                                      <div key={idx} className="flex justify-between text-sm">
                                        <span className="text-gray-300">{item.name}</span>
                                        <span className="text-white font-medium">₹{item.amount?.toLocaleString('en-IN')}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Metrics */}
                          <div className="flex lg:flex-col gap-4 lg:gap-2 lg:text-right lg:min-w-[140px]">
                            <div>
                              <div className="text-gray-400 text-xs">Amount</div>
                              <div className={`font-bold text-lg ${colors.text}`}>₹{leak.amount?.toLocaleString('en-IN')}</div>
                            </div>
                            {leak.percentage_of_income > 0 && (
                              <div>
                                <div className="text-gray-400 text-xs">% of Income</div>
                                <div className="text-white font-bold">{leak.percentage_of_income}%</div>
                              </div>
                            )}
                            {leak.count > 0 && (
                              <div>
                                <div className="text-gray-400 text-xs">Count</div>
                                <div className="text-white font-bold">{leak.count} items</div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 mb-6">
                  <FaCheckCircle className="text-5xl mx-auto mb-4 text-green-400" />
                  <h4 className="text-white font-bold text-xl mb-2">No Cash Leaks Detected!</h4>
                  <p className="text-gray-400">Your spending habits are well-optimized. Keep up the great work!</p>
                </div>
              )}

              {/* Action Suggestions */}
              {cashLeaks.suggestions && cashLeaks.suggestions.length > 0 && (
                <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl p-4 border border-blue-500/20">
                  <h4 className="text-blue-400 font-bold mb-4 flex items-center gap-2">
                    <FaLightbulb />
                    Action Suggestions
                  </h4>
                  <div className="space-y-3">
                    {cashLeaks.suggestions.map((suggestion, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-[#0F172A]/50 rounded-lg">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          suggestion.priority === 1 ? 'bg-red-500/20' : 'bg-blue-500/20'
                        }`}>
                          <span className={`font-bold ${suggestion.priority === 1 ? 'text-red-400' : 'text-blue-400'}`}>
                            {idx + 1}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h5 className="text-white font-bold">{suggestion.action}</h5>
                            {suggestion.priority === 1 && (
                              <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full">HIGH PRIORITY</span>
                            )}
                          </div>
                          <p className="text-gray-400 text-sm mt-1">{suggestion.detail}</p>
                          {suggestion.potential_savings > 0 && (
                            <div className="mt-2 text-green-400 text-sm font-medium">
                              💰 Potential savings: ₹{suggestion.potential_savings?.toLocaleString('en-IN')}/month
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <FaSearch className="text-4xl mx-auto mb-3 opacity-50" />
              <p>{cashLeaks?.message || 'Save your budget to detect cash leaks'}</p>
              <p className="text-sm mt-2">Add your income and expenses, then click "Save Budget"</p>
            </div>
          )}
        </div>
      )}

      {/* Family Mode Tab */}
      {activeTab === 'family' && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-white font-bold flex items-center gap-2">
                <FaUserFriends className="text-pink-400" />
                Family Financial Dashboard
              </h3>
              <p className="text-gray-400 text-sm mt-1">
                Joint finances, shared planning & "What My Family Should Know" snapshot.
              </p>
            </div>
          </div>
          
          {/* Family Members Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-gray-300 font-medium">Family Members</h4>
              {!isAdvisorOrManager && (
                <button
                  onClick={() => setShowAddMember(true)}
                  className="px-3 py-1.5 bg-pink-500 hover:bg-pink-600 text-white rounded-lg flex items-center gap-2 text-sm transition-colors"
                  data-testid="add-family-member-btn"
                >
                  <FaPlus /> Add Member
                </button>
              )}
            </div>
            
            {/* Add Member Form */}
            {showAddMember && (
              <div className="bg-[#0F172A] rounded-lg p-4 mb-4 border border-pink-500/30">
                <div className="grid md:grid-cols-3 gap-4 mb-4">
                  <input
                    type="text"
                    placeholder="Name *"
                    value={newMember.name}
                    onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                    className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                    data-testid="member-name-input"
                  />
                  <select
                    value={newMember.relation}
                    onChange={(e) => setNewMember({ ...newMember, relation: e.target.value })}
                    className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                    data-testid="member-relation-select"
                  >
                    {(familyOptions?.relation_types || ['Self', 'Spouse', 'Father', 'Mother', 'Son', 'Daughter', 'Brother', 'Sister', 'Other']).map(rel => (
                      <option key={rel} value={rel}>{rel}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    placeholder="Age"
                    value={newMember.age}
                    onChange={(e) => setNewMember({ ...newMember, age: e.target.value })}
                    className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                    data-testid="member-age-input"
                  />
                </div>
                <div className="grid md:grid-cols-3 gap-4 mb-4">
                  <input
                    type="text"
                    placeholder="Occupation"
                    value={newMember.occupation}
                    onChange={(e) => setNewMember({ ...newMember, occupation: e.target.value })}
                    className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                  />
                  <label className="flex items-center gap-2 text-gray-300">
                    <input
                      type="checkbox"
                      checked={newMember.is_earning}
                      onChange={(e) => setNewMember({ ...newMember, is_earning: e.target.checked })}
                      className="w-4 h-4"
                      data-testid="member-earning-checkbox"
                    />
                    Earning Member
                  </label>
                  {newMember.is_earning && (
                    <input
                      type="number"
                      placeholder="Monthly Income (₹)"
                      value={newMember.income_contribution}
                      onChange={(e) => setNewMember({ ...newMember, income_contribution: e.target.value })}
                      className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                      data-testid="member-income-input"
                    />
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={addFamilyMember}
                    disabled={savingFamily || !newMember.name}
                    className="px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
                    data-testid="save-member-btn"
                  >
                    {savingFamily ? <FaSpinner className="animate-spin" /> : <FaSave />}
                    Save
                  </button>
                  <button
                    onClick={() => setShowAddMember(false)}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            
            {/* Family Members List */}
            {familyData?.family_members?.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {familyData.family_members.map((member, idx) => (
                  <div key={member.id || idx} className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F] relative group">
                    {!isAdvisorOrManager && (
                      <button
                        onClick={() => deleteFamilyMember(member.id)}
                        className="absolute top-2 right-2 text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`delete-member-${idx}`}
                      >
                        <FaTrash size={14} />
                      </button>
                    )}
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        member.is_earning ? 'bg-green-500/20' : 'bg-gray-500/20'
                      }`}>
                        <FaUsers className={member.is_earning ? 'text-green-400' : 'text-gray-400'} />
                      </div>
                      <div>
                        <div className="text-white font-medium">{member.name || member.relation}</div>
                        <div className="text-gray-400 text-xs">{member.relation}{member.age ? `, ${member.age} yrs` : ''}</div>
                      </div>
                    </div>
                    {member.occupation && (
                      <div className="text-gray-400 text-xs mb-1">{member.occupation}</div>
                    )}
                    {member.is_earning && (
                      <div className="text-green-400 text-sm font-medium">
                        ₹{member.income_contribution?.toLocaleString('en-IN')}/month
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-400 bg-[#0F172A] rounded-lg">
                <FaUsers className="text-3xl mx-auto mb-2 opacity-50" />
                <p className="text-sm">No family members added yet. Click "Add Member" to get started.</p>
              </div>
            )}
          </div>

          {/* Joint Income Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-xl p-4 border border-green-500/30">
              <div className="text-green-400 text-sm mb-1">Total Family Income</div>
              <div className="text-2xl font-bold text-white">
                ₹{(familyData?.total_family_income || 0).toLocaleString('en-IN')}
              </div>
              <div className="text-gray-400 text-xs">/month</div>
            </div>
            <div className="bg-[#0F172A] rounded-xl p-4 border border-[#2D5A8F]">
              <div className="text-gray-400 text-sm mb-1">Earning Members</div>
              <div className="text-2xl font-bold text-white">{familyData?.earning_members_count || 0}</div>
            </div>
            <div className="bg-[#0F172A] rounded-xl p-4 border border-[#2D5A8F]">
              <div className="text-gray-400 text-sm mb-1">Dependents</div>
              <div className="text-2xl font-bold text-white">{familyData?.total_dependents || 0}</div>
            </div>
            <div className="bg-gradient-to-br from-red-500/20 to-red-600/10 rounded-xl p-4 border border-red-500/30">
              <div className="text-red-400 text-sm mb-1">Shared Expenses</div>
              <div className="text-2xl font-bold text-white">
                ₹{(familyData?.total_shared_expenses || 0).toLocaleString('en-IN')}
              </div>
              <div className="text-gray-400 text-xs">/month</div>
            </div>
          </div>

          {/* Shared Expenses Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-gray-300 font-medium">Shared Family Expenses</h4>
              {!isAdvisorOrManager && (
                <button
                  onClick={() => setShowAddSharedExpense(true)}
                  className="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2 text-sm transition-colors"
                  data-testid="add-shared-expense-btn"
                >
                  <FaPlus /> Add Expense
                </button>
              )}
            </div>
            
            {/* Add Shared Expense Form */}
            {showAddSharedExpense && (
              <div className="bg-[#0F172A] rounded-lg p-4 mb-4 border border-orange-500/30">
                <div className="grid md:grid-cols-4 gap-4 mb-4">
                  <select
                    value={newSharedExpense.category}
                    onChange={(e) => setNewSharedExpense({ ...newSharedExpense, category: e.target.value })}
                    className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                  >
                    {(familyOptions?.expense_categories || ['Rent/Housing', 'Groceries', 'Utilities', 'Education', 'Medical', 'Insurance Premium', 'Vehicle/Transport', 'Domestic Help', 'Entertainment', 'Other']).map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Description *"
                    value={newSharedExpense.description}
                    onChange={(e) => setNewSharedExpense({ ...newSharedExpense, description: e.target.value })}
                    className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                  />
                  <input
                    type="number"
                    placeholder="Amount (₹) *"
                    value={newSharedExpense.amount}
                    onChange={(e) => setNewSharedExpense({ ...newSharedExpense, amount: e.target.value })}
                    className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                  />
                  <select
                    value={newSharedExpense.frequency}
                    onChange={(e) => setNewSharedExpense({ ...newSharedExpense, frequency: e.target.value })}
                    className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="half-yearly">Half-Yearly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={addSharedExpense}
                    disabled={savingFamily || !newSharedExpense.description || !newSharedExpense.amount}
                    className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
                  >
                    {savingFamily ? <FaSpinner className="animate-spin" /> : <FaSave />}
                    Save
                  </button>
                  <button
                    onClick={() => setShowAddSharedExpense(false)}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            
            {/* Shared Expenses List */}
            {familyData?.shared_expenses?.length > 0 ? (
              <div className="space-y-2">
                {familyData.shared_expenses.map((expense, idx) => (
                  <div key={expense.id || idx} className="flex items-center justify-between bg-[#0F172A] rounded-lg p-3 border border-[#2D5A8F]">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                        <FaCreditCard className="text-orange-400" />
                      </div>
                      <div>
                        <div className="text-white font-medium">{expense.description}</div>
                        <div className="text-gray-400 text-xs">{expense.category} • {expense.frequency}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-orange-400 font-bold">₹{expense.amount?.toLocaleString('en-IN')}</span>
                      {!isAdvisorOrManager && (
                        <button
                          onClick={() => deleteSharedExpense(expense.id)}
                          className="text-red-400 hover:text-red-300 p-2"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-gray-400 bg-[#0F172A] rounded-lg text-sm">
                No shared expenses added yet.
              </div>
            )}
          </div>

          {/* Insurance Coverage Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-gray-300 font-medium">Insurance Coverage</h4>
              {!isAdvisorOrManager && (
                <button
                  onClick={() => setShowAddInsurance(true)}
                  className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 text-sm transition-colors"
                  data-testid="add-insurance-btn"
                >
                  <FaPlus /> Add Policy
                </button>
              )}
            </div>
            
            {/* Add Insurance Form */}
            {showAddInsurance && (
              <div className="bg-[#0F172A] rounded-lg p-4 mb-4 border border-blue-500/30">
                <div className="grid md:grid-cols-4 gap-4 mb-4">
                  <select
                    value={newInsurance.type}
                    onChange={(e) => setNewInsurance({ ...newInsurance, type: e.target.value })}
                    className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                  >
                    <option value="life">Life Insurance</option>
                    <option value="health">Health Insurance</option>
                    <option value="term">Term Insurance</option>
                    <option value="ulip">ULIP</option>
                    <option value="endowment">Endowment</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Policy Name *"
                    value={newInsurance.policy_name}
                    onChange={(e) => setNewInsurance({ ...newInsurance, policy_name: e.target.value })}
                    className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                  />
                  <input
                    type="text"
                    placeholder="Policy Number"
                    value={newInsurance.policy_number}
                    onChange={(e) => setNewInsurance({ ...newInsurance, policy_number: e.target.value })}
                    className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                  />
                  <input
                    type="text"
                    placeholder="Provider (LIC, HDFC, etc.)"
                    value={newInsurance.provider}
                    onChange={(e) => setNewInsurance({ ...newInsurance, provider: e.target.value })}
                    className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                  />
                </div>
                <div className="grid md:grid-cols-4 gap-4 mb-4">
                  <input
                    type="number"
                    placeholder="Coverage Amount (₹) *"
                    value={newInsurance.coverage_amount}
                    onChange={(e) => setNewInsurance({ ...newInsurance, coverage_amount: e.target.value })}
                    className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                  />
                  <input
                    type="number"
                    placeholder="Premium Amount (₹)"
                    value={newInsurance.premium_amount}
                    onChange={(e) => setNewInsurance({ ...newInsurance, premium_amount: e.target.value })}
                    className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                  />
                  <select
                    value={newInsurance.premium_frequency}
                    onChange={(e) => setNewInsurance({ ...newInsurance, premium_frequency: e.target.value })}
                    className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="half-yearly">Half-Yearly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                  <label className="flex items-center gap-2 text-gray-300">
                    <input
                      type="checkbox"
                      checked={newInsurance.is_family_floater}
                      onChange={(e) => setNewInsurance({ ...newInsurance, is_family_floater: e.target.checked })}
                      className="w-4 h-4"
                    />
                    Family Floater
                  </label>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={addInsurancePolicy}
                    disabled={savingFamily || !newInsurance.policy_name || !newInsurance.coverage_amount}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
                  >
                    {savingFamily ? <FaSpinner className="animate-spin" /> : <FaSave />}
                    Save
                  </button>
                  <button
                    onClick={() => setShowAddInsurance(false)}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            
            {/* Insurance Summary Cards */}
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-[#0F172A] rounded-lg p-4 border border-blue-500/30">
                <div className="flex items-center gap-2 mb-3">
                  <FaShieldAlt className="text-blue-400" />
                  <span className="text-white font-medium">Life Insurance</span>
                </div>
                <div className="text-2xl font-bold text-white mb-1">
                  ₹{(familyData?.insurance_summary?.life_insurance?.total_coverage || 0).toLocaleString('en-IN')}
                </div>
                {familyData?.insurance_summary?.life_insurance?.gap > 0 && (
                  <div className="text-red-400 text-sm">
                    Gap: ₹{familyData.insurance_summary.life_insurance.gap.toLocaleString('en-IN')}
                  </div>
                )}
                {familyData?.insurance_summary?.life_insurance?.recommended > 0 && (
                  <div className="text-gray-400 text-xs mt-1">
                    Recommended: ₹{familyData.insurance_summary.life_insurance.recommended.toLocaleString('en-IN')}
                  </div>
                )}
              </div>
              <div className="bg-[#0F172A] rounded-lg p-4 border border-green-500/30">
                <div className="flex items-center gap-2 mb-3">
                  <FaHeart className="text-green-400" />
                  <span className="text-white font-medium">Health Insurance</span>
                </div>
                <div className="text-2xl font-bold text-white mb-1">
                  ₹{(familyData?.insurance_summary?.health_insurance?.total_coverage || 0).toLocaleString('en-IN')}
                </div>
                {familyData?.insurance_summary?.health_insurance?.family_floater && (
                  <div className="text-green-400 text-sm">Family Floater Active</div>
                )}
              </div>
            </div>
            
            {/* Insurance Policies List */}
            {(familyData?.insurance_summary?.life_insurance?.policies?.length > 0 || 
              familyData?.insurance_summary?.health_insurance?.policies?.length > 0) && (
              <div className="space-y-2">
                {[...(familyData?.insurance_summary?.life_insurance?.policies || []),
                  ...(familyData?.insurance_summary?.health_insurance?.policies || [])].map((policy, idx) => (
                  <div key={policy.id || idx} className="flex items-center justify-between bg-[#0F172A]/60 rounded-lg p-3 border border-[#2D5A8F]/50">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        policy.type === 'health' ? 'bg-green-500/20' : 'bg-blue-500/20'
                      }`}>
                        {policy.type === 'health' ? <FaHeart className="text-green-400" /> : <FaShieldAlt className="text-blue-400" />}
                      </div>
                      <div>
                        <div className="text-white font-medium">{policy.policy_name}</div>
                        <div className="text-gray-400 text-xs">
                          {policy.provider} • {policy.type?.toUpperCase()} {policy.is_family_floater && '• Family Floater'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-white font-bold">₹{policy.coverage_amount?.toLocaleString('en-IN')}</div>
                        {policy.premium_amount > 0 && (
                          <div className="text-gray-400 text-xs">₹{policy.premium_amount?.toLocaleString('en-IN')}/{policy.premium_frequency}</div>
                        )}
                      </div>
                      {!isAdvisorOrManager && (
                        <button
                          onClick={() => deleteInsurancePolicy(policy.id)}
                          className="text-red-400 hover:text-red-300 p-2"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Nominee Overview Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-gray-300 font-medium">Nominee Overview</h4>
              {!isAdvisorOrManager && (
                <button
                  onClick={() => setShowAddNominee(true)}
                  className="px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded-lg flex items-center gap-2 text-sm transition-colors"
                  data-testid="add-nominee-btn"
                >
                  <FaPlus /> Add Nominee
                </button>
              )}
            </div>
            
            {/* Add Nominee Form */}
            {showAddNominee && (
              <div className="bg-[#0F172A] rounded-lg p-4 mb-4 border border-purple-500/30">
                <div className="grid md:grid-cols-5 gap-4 mb-4">
                  <select
                    value={newNominee.asset_type}
                    onChange={(e) => setNewNominee({ ...newNominee, asset_type: e.target.value })}
                    className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                  >
                    {(familyOptions?.asset_types || ['Bank Account', 'Fixed Deposit', 'Mutual Fund', 'PPF', 'EPF', 'NPS', 'Life Insurance', 'Property', 'Gold', 'Stocks', 'Demat Account', 'LIC Policy', 'Other']).map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Asset Name/Account *"
                    value={newNominee.asset_name}
                    onChange={(e) => setNewNominee({ ...newNominee, asset_name: e.target.value })}
                    className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                  />
                  <input
                    type="text"
                    placeholder="Nominee Name *"
                    value={newNominee.nominee}
                    onChange={(e) => setNewNominee({ ...newNominee, nominee: e.target.value })}
                    className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                  />
                  <select
                    value={newNominee.relation}
                    onChange={(e) => setNewNominee({ ...newNominee, relation: e.target.value })}
                    className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                  >
                    {(familyOptions?.relation_types || ['Self', 'Spouse', 'Father', 'Mother', 'Son', 'Daughter', 'Brother', 'Sister', 'Other']).map(rel => (
                      <option key={rel} value={rel}>{rel}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    placeholder="Share %"
                    value={newNominee.share_percentage}
                    onChange={(e) => setNewNominee({ ...newNominee, share_percentage: e.target.value })}
                    className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                    min="0"
                    max="100"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={addNominee}
                    disabled={savingFamily || !newNominee.asset_name || !newNominee.nominee}
                    className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
                  >
                    {savingFamily ? <FaSpinner className="animate-spin" /> : <FaSave />}
                    Save
                  </button>
                  <button
                    onClick={() => setShowAddNominee(false)}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            
            {/* Nominee Table */}
            {familyData?.nominee_summary?.length > 0 ? (
              <div className="bg-[#0F172A] rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#2D5A8F]">
                      <th className="text-left text-gray-400 p-3">Asset Type</th>
                      <th className="text-left text-gray-400 p-3">Asset Name</th>
                      <th className="text-left text-gray-400 p-3">Nominee</th>
                      <th className="text-left text-gray-400 p-3">Relation</th>
                      <th className="text-left text-gray-400 p-3">Share</th>
                      {!isAdvisorOrManager && <th className="text-gray-400 p-3 w-10"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {familyData.nominee_summary.map((nominee, idx) => (
                      <tr key={nominee.id || idx} className="border-b border-[#2D5A8F]/50 hover:bg-[#1A2942]/50">
                        <td className="p-3 text-gray-400">{nominee.asset_type}</td>
                        <td className="p-3 text-white">{nominee.asset_name}</td>
                        <td className="p-3 text-gray-300">{nominee.nominee}</td>
                        <td className="p-3 text-gray-400">{nominee.relation}</td>
                        <td className="p-3 text-white">{nominee.share_percentage}%</td>
                        {!isAdvisorOrManager && (
                          <td className="p-3">
                            <button
                              onClick={() => deleteNominee(nominee.id)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <FaTrash size={14} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-4 text-gray-400 bg-[#0F172A] rounded-lg text-sm">
                No nominees added yet. Add nominees for your assets to ensure smooth transfer.
              </div>
            )}
          </div>

          {/* What My Family Should Know Section */}
          <div className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 rounded-xl p-4 border border-pink-500/20">
            <h4 className="text-pink-400 font-bold mb-3 flex items-center gap-2">
              <FaHeart />
              What My Family Should Know
            </h4>
            <p className="text-gray-400 text-sm mb-4">
              Important documents and information your family should have access to in case of emergency.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="text-gray-300 text-sm font-medium mb-2">Important Documents</div>
                <div className="flex flex-wrap gap-2">
                  {(familyData?.family_snapshot?.important_documents?.length > 0) ? (
                    familyData.family_snapshot.important_documents.map((doc, idx) => (
                      <span key={idx} className="px-3 py-1 bg-[#0F172A] rounded-full text-xs text-gray-400 border border-[#2D5A8F]">
                        {doc}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-500 text-sm">No documents listed</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-gray-300 text-sm font-medium mb-2">Key Actions</div>
                <div className="space-y-2 text-sm text-gray-400">
                  <div className="flex items-center gap-2">
                    <FaCheckCircle className={familyData?.nominee_summary?.length > 0 ? 'text-green-400' : 'text-gray-600'} />
                    {familyData?.nominee_summary?.length > 0 ? 'Nominees updated' : 'Update nominees in all investments'}
                  </div>
                  <div className="flex items-center gap-2">
                    <FaCheckCircle className={familyData?.insurance_summary?.life_insurance?.total_coverage > 0 ? 'text-green-400' : 'text-gray-600'} />
                    {familyData?.insurance_summary?.life_insurance?.total_coverage > 0 ? 'Life insurance in place' : 'Get adequate life insurance'}
                  </div>
                  <div className="flex items-center gap-2">
                    <FaInfoCircle className="text-yellow-400" />
                    Share login credentials securely with family
                  </div>
                  <div className="flex items-center gap-2">
                    <FaInfoCircle className="text-yellow-400" />
                    Consider creating a will for asset distribution
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Behavioural Insights Tab */}
      {activeTab === 'insights' && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <h3 className="text-white font-bold mb-4 flex items-center gap-2">
            <FaBell className="text-cyan-400" />
            Behavioural Insights & Alerts
          </h3>
          <p className="text-gray-400 text-sm mb-6">
            Track spending patterns, get alerts, and year-over-year comparisons.
          </p>
          
          {behaviouralInsights ? (
            <>
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-orange-500/10 rounded-lg p-4 border border-orange-500/20 text-center">
                  <div className="text-3xl font-bold text-orange-400">{behaviouralInsights.summary?.total_alerts || 0}</div>
                  <div className="text-gray-400 text-sm">Total Alerts</div>
                </div>
                <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/20 text-center">
                  <div className="text-3xl font-bold text-red-400">{behaviouralInsights.summary?.action_required || 0}</div>
                  <div className="text-gray-400 text-sm">Action Required</div>
                </div>
                <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/20 text-center">
                  <div className="text-3xl font-bold text-green-400">{behaviouralInsights.summary?.positive_insights || 0}</div>
                  <div className="text-gray-400 text-sm">Positive Insights</div>
                </div>
              </div>

              {/* Alerts */}
              {behaviouralInsights.alerts?.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-gray-300 font-medium mb-3">Alerts & Warnings</h4>
                  <div className="space-y-3">
                    {behaviouralInsights.alerts.map((alert, idx) => {
                      const IconComponent = {
                        'FaArrowUp': FaArrowUp,
                        'FaGift': FaGift,
                        'FaChartLine': FaChartLine,
                        'FaPiggyBank': FaPiggyBank,
                        'FaBell': FaBell
                      }[alert.icon] || FaExclamationTriangle;
                      
                      const colorClasses = {
                        'orange': 'bg-orange-500/20 border-orange-500/30 text-orange-400',
                        'red': 'bg-red-500/20 border-red-500/30 text-red-400',
                        'yellow': 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400',
                        'blue': 'bg-blue-500/20 border-blue-500/30 text-blue-400'
                      }[alert.color] || 'bg-gray-500/20 border-gray-500/30 text-gray-400';
                      
                      return (
                        <div key={idx} className={`rounded-xl p-4 border ${colorClasses.split(' ').slice(0, 2).join(' ')}`}>
                          <div className="flex items-start gap-4">
                            <div className={`w-10 h-10 rounded-lg ${colorClasses.split(' ')[0]} flex items-center justify-center flex-shrink-0`}>
                              <IconComponent className={colorClasses.split(' ')[2]} />
                            </div>
                            <div className="flex-1">
                              <h5 className="text-white font-medium mb-1">{alert.title}</h5>
                              <p className="text-gray-400 text-sm mb-2">{alert.message}</p>
                              {alert.recommendation && (
                                <p className="text-gray-300 text-sm italic">
                                  <FaLightbulb className="inline mr-1 text-yellow-400" />
                                  {alert.recommendation}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Positive Insights */}
              {behaviouralInsights.insights?.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-gray-300 font-medium mb-3">Positive Insights</h4>
                  <div className="space-y-3">
                    {behaviouralInsights.insights.map((insight, idx) => (
                      <div key={idx} className="bg-green-500/10 rounded-xl p-4 border border-green-500/20">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                            <FaThumbsUp className="text-green-400" />
                          </div>
                          <div>
                            <h5 className="text-white font-medium mb-1">{insight.title}</h5>
                            <p className="text-gray-400 text-sm">{insight.message}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Year-over-Year Comparison */}
              {behaviouralInsights.yoy_comparison && (
                <div className="mb-6">
                  <h4 className="text-gray-300 font-medium mb-3">Year-over-Year Comparison</h4>
                  <div className="bg-[#0F172A] rounded-xl p-4 border border-[#2D5A8F]">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-gray-400 text-sm mb-1">Last Year</div>
                        <div className="text-xl font-bold text-white">
                          ₹{behaviouralInsights.yoy_comparison.last_year_expenses?.toLocaleString('en-IN')}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-sm mb-1">This Year</div>
                        <div className="text-xl font-bold text-white">
                          ₹{behaviouralInsights.yoy_comparison.current_expenses?.toLocaleString('en-IN')}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-sm mb-1">Change</div>
                        <div className={`text-xl font-bold ${
                          behaviouralInsights.yoy_comparison.change_percent > 0 ? 'text-red-400' : 'text-green-400'
                        }`}>
                          {behaviouralInsights.yoy_comparison.change_percent > 0 ? '+' : ''}
                          {behaviouralInsights.yoy_comparison.change_percent}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Month-over-Month Comparison */}
              {behaviouralInsights.mom_comparison && (
                <div className="mb-6">
                  <h4 className="text-gray-300 font-medium mb-3">Month-over-Month Comparison</h4>
                  <div className="bg-[#0F172A] rounded-xl p-4 border border-[#2D5A8F]">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-gray-400 text-sm mb-1">Previous Month</div>
                        <div className="text-xl font-bold text-white">
                          ₹{behaviouralInsights.mom_comparison.previous_expenses?.toLocaleString('en-IN')}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-sm mb-1">This Month</div>
                        <div className="text-xl font-bold text-white">
                          ₹{behaviouralInsights.mom_comparison.current_expenses?.toLocaleString('en-IN')}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-sm mb-1">Change</div>
                        <div className={`text-xl font-bold ${
                          behaviouralInsights.mom_comparison.change_percent > 0 ? 'text-red-400' : 'text-green-400'
                        }`}>
                          {behaviouralInsights.mom_comparison.change_percent > 0 ? '+' : ''}
                          {behaviouralInsights.mom_comparison.change_percent}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Spending Breakdown */}
              {behaviouralInsights.spending_breakdown && (
                <div className="mb-6">
                  <h4 className="text-gray-300 font-medium mb-3">Spending Breakdown</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    <div className="bg-[#0F172A] rounded-lg p-3 border border-blue-500/30">
                      <div className="text-blue-400 text-xs mb-1">Essentials</div>
                      <div className="text-white font-bold">₹{behaviouralInsights.spending_breakdown.essential_spending?.toLocaleString('en-IN')}</div>
                    </div>
                    <div className="bg-[#0F172A] rounded-lg p-3 border border-purple-500/30">
                      <div className="text-purple-400 text-xs mb-1">Lifestyle</div>
                      <div className="text-white font-bold">₹{behaviouralInsights.spending_breakdown.lifestyle_spending?.toLocaleString('en-IN')}</div>
                    </div>
                    <div className="bg-[#0F172A] rounded-lg p-3 border border-orange-500/30">
                      <div className="text-orange-400 text-xs mb-1">EMIs</div>
                      <div className="text-white font-bold">₹{behaviouralInsights.spending_breakdown.emi_spending?.toLocaleString('en-IN')}</div>
                    </div>
                    <div className={`bg-[#0F172A] rounded-lg p-3 border ${behaviouralInsights.spending_breakdown.savings >= 0 ? 'border-green-500/30' : 'border-red-500/30'}`}>
                      <div className={`text-xs mb-1 ${behaviouralInsights.spending_breakdown.savings >= 0 ? 'text-green-400' : 'text-red-400'}`}>Savings</div>
                      <div className="text-white font-bold">₹{behaviouralInsights.spending_breakdown.savings?.toLocaleString('en-IN')}</div>
                    </div>
                    <div className={`bg-[#0F172A] rounded-lg p-3 border ${behaviouralInsights.spending_breakdown.savings_rate >= 20 ? 'border-green-500/30' : 'border-yellow-500/30'}`}>
                      <div className={`text-xs mb-1 ${behaviouralInsights.spending_breakdown.savings_rate >= 20 ? 'text-green-400' : 'text-yellow-400'}`}>Savings Rate</div>
                      <div className="text-white font-bold">{behaviouralInsights.spending_breakdown.savings_rate}%</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Premium Reminders */}
              {behaviouralInsights.premium_reminders?.length > 0 && (
                <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/20">
                  <h4 className="text-yellow-400 font-bold mb-3 flex items-center gap-2">
                    <FaBell />
                    Premium Due Reminders
                  </h4>
                  <div className="space-y-2">
                    {behaviouralInsights.premium_reminders.map((reminder, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-[#0F172A] rounded-lg p-3">
                        <div>
                          <div className="text-white font-medium">{reminder.type} - {reminder.company}</div>
                          <div className="text-gray-400 text-sm">Policy: {reminder.policy_number}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-yellow-400 font-bold">₹{reminder.amount?.toLocaleString('en-IN')}</div>
                          <div className={`text-sm ${reminder.urgency === 'urgent' ? 'text-red-400' : 'text-yellow-400'}`}>
                            Due in {reminder.days_until_due} days
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <FaBell className="text-4xl mx-auto mb-3 opacity-50" />
              <p>Save your budget to see behavioural insights</p>
            </div>
          )}
        </div>
      )}

      {/* Tax Awareness Tab */}
      {activeTab === 'tax' && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <h3 className="text-white font-bold mb-4 flex items-center gap-2">
            <FaFileInvoiceDollar className="text-teal-400" />
            Tax Awareness - FY {taxData?.financial_year || getCurrentFinancialYear()}
          </h3>
          <p className="text-gray-400 text-sm mb-6">
            Track your tax-saving investments and maximize deductions under various sections.
          </p>
          
          {taxData ? (
            <>
              {/* Tax Savings Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-teal-500/20 to-teal-600/10 rounded-xl p-4 border border-teal-500/30">
                  <div className="text-teal-400 text-sm mb-1">Total Deductions</div>
                  <div className="text-2xl font-bold text-white">
                    ₹{taxData.total_deductions?.toLocaleString('en-IN')}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-xl p-4 border border-green-500/30">
                  <div className="text-green-400 text-sm mb-1">Est. Tax Saved</div>
                  <div className="text-2xl font-bold text-white">
                    ₹{taxData.estimated_tax_saved?.toLocaleString('en-IN')}
                  </div>
                </div>
                <div className="bg-[#0F172A] rounded-xl p-4 border border-[#2D5A8F]">
                  <div className="text-gray-400 text-sm mb-1">Available Limit</div>
                  <div className="text-2xl font-bold text-white">
                    ₹{taxData.summary?.total_limit_available?.toLocaleString('en-IN')}
                  </div>
                </div>
                <div className="bg-[#0F172A] rounded-xl p-4 border border-[#2D5A8F]">
                  <div className="text-gray-400 text-sm mb-1">Optimization Potential</div>
                  <div className="text-2xl font-bold text-yellow-400">
                    ₹{taxData.summary?.optimization_potential?.toLocaleString('en-IN')}
                  </div>
                </div>
              </div>

              {/* Section 80C */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-medium">Section 80C (Limit: ₹1,50,000)</h4>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-sm ${
                      taxData.section_80c?.utilization_percent >= 100 ? 'bg-green-500/20 text-green-400' :
                      taxData.section_80c?.utilization_percent >= 50 ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {taxData.section_80c?.utilization_percent}% Utilized
                    </span>
                    {!isAdvisorOrManager && (
                      <button
                        onClick={() => setShowAdd80C(true)}
                        className="px-3 py-1.5 bg-teal-500 hover:bg-teal-600 text-white rounded-lg flex items-center gap-2 text-sm transition-colors"
                        data-testid="add-80c-btn"
                      >
                        <FaPlus /> Add Investment
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Add 80C Form */}
                {showAdd80C && (
                  <div className="bg-[#0F172A] rounded-lg p-4 mb-4 border border-teal-500/30">
                    <div className="grid md:grid-cols-4 gap-4 mb-4">
                      <select
                        value={new80C.key}
                        onChange={(e) => {
                          const option = SECTION_80C_OPTIONS.find(o => o.key === e.target.value);
                          setNew80C({ ...new80C, key: e.target.value, name: option?.name || '', description: option?.description || '' });
                        }}
                        className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                        data-testid="80c-type-select"
                      >
                        {SECTION_80C_OPTIONS.map(opt => (
                          <option key={opt.key} value={opt.key}>{opt.name}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder="Investment Name"
                        value={new80C.name}
                        onChange={(e) => setNew80C({ ...new80C, name: e.target.value })}
                        className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                      />
                      <input
                        type="number"
                        placeholder="Amount (₹) *"
                        value={new80C.amount}
                        onChange={(e) => setNew80C({ ...new80C, amount: e.target.value })}
                        className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                        data-testid="80c-amount-input"
                      />
                      <input
                        type="text"
                        placeholder="Description (optional)"
                        value={new80C.description}
                        onChange={(e) => setNew80C({ ...new80C, description: e.target.value })}
                        className="bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-3 py-2 text-white"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={add80CComponent}
                        disabled={savingTax || !new80C.amount}
                        className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
                        data-testid="save-80c-btn"
                      >
                        {savingTax ? <FaSpinner className="animate-spin" /> : <FaSave />}
                        Save
                      </button>
                      <button
                        onClick={() => setShowAdd80C(false)}
                        className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="bg-[#0F172A] rounded-xl p-4 border border-[#2D5A8F]">
                  <div className="h-4 bg-[#1A2942] rounded-full mb-4">
                    <div 
                      className="h-full bg-gradient-to-r from-teal-500 to-green-500 rounded-full transition-all"
                      style={{ width: `${Math.min(100, taxData.section_80c?.utilization_percent || 0)}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-sm mb-4">
                    <span className="text-gray-400">Claimed: ₹{taxData.section_80c?.total_claimed?.toLocaleString('en-IN')}</span>
                    <span className="text-green-400">Remaining: ₹{taxData.section_80c?.remaining?.toLocaleString('en-IN')}</span>
                  </div>
                  {taxData.section_80c?.components?.length > 0 ? (
                    <div className="space-y-2">
                      {taxData.section_80c.components.map((comp, idx) => (
                        <div key={comp.id || idx} className="flex justify-between items-center py-2 border-t border-[#2D5A8F]/50 group">
                          <div>
                            <div className="text-white text-sm flex items-center gap-2">
                              {comp.name}
                              {comp.auto_detected && (
                                <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">Auto-detected</span>
                              )}
                            </div>
                            <div className="text-gray-400 text-xs">{comp.description}</div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-teal-400 font-medium">₹{comp.amount?.toLocaleString('en-IN')}</div>
                            {!isAdvisorOrManager && !comp.auto_detected && comp.id && (
                              <button
                                onClick={() => delete80CComponent(comp.id)}
                                className="text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <FaTrash size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-400 text-sm">
                      No 80C investments recorded yet. Click "Add Investment" to start tracking.
                    </div>
                  )}
                </div>
              </div>

              {/* Section 80D */}
              <div className="mb-6">
                <h4 className="text-white font-medium mb-3">Section 80D - Health Insurance</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
                    <div className="text-gray-400 text-sm mb-1">Self & Family (Limit: ₹25,000)</div>
                    <div className="text-xl font-bold text-white mb-2">
                      ₹{taxData.section_80d?.self_family_claimed?.toLocaleString('en-IN')}
                    </div>
                    <div className="h-2 bg-[#1A2942] rounded-full">
                      <div 
                        className="h-full bg-blue-500 rounded-full"
                        style={{ width: `${(taxData.section_80d?.self_family_claimed / 25000) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
                    <div className="text-gray-400 text-sm mb-1">Parents (Limit: ₹50,000)</div>
                    <div className="text-xl font-bold text-white mb-2">
                      ₹{taxData.section_80d?.parents_claimed?.toLocaleString('en-IN')}
                    </div>
                    <div className="h-2 bg-[#1A2942] rounded-full">
                      <div 
                        className="h-full bg-green-500 rounded-full"
                        style={{ width: `${(taxData.section_80d?.parents_claimed / 50000) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Other Sections */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                {/* Section 24 */}
                <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
                  <h4 className="text-white font-medium mb-2">Section 24 - Home Loan Interest</h4>
                  <div className="text-gray-400 text-sm mb-1">Limit: ₹2,00,000</div>
                  <div className="text-xl font-bold text-white">
                    ₹{taxData.section_24?.claimed?.toLocaleString('en-IN')}
                  </div>
                </div>
                {/* Section 80CCD */}
                <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
                  <h4 className="text-white font-medium mb-2">Section 80CCD(1B) - NPS</h4>
                  <div className="text-gray-400 text-sm mb-1">Additional Limit: ₹50,000</div>
                  <div className="text-xl font-bold text-white">
                    ₹{taxData.section_80ccd?.claimed?.toLocaleString('en-IN')}
                  </div>
                </div>
              </div>

              {/* Interpretations */}
              {taxData.interpretations?.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-gray-300 font-medium mb-3">Tax Status Insights</h4>
                  <div className="grid md:grid-cols-2 gap-3">
                    {taxData.interpretations.map((interp, idx) => {
                      const bgColor = {
                        'success': 'bg-green-500/10 border-green-500/30',
                        'warning': 'bg-yellow-500/10 border-yellow-500/30',
                        'error': 'bg-red-500/10 border-red-500/30',
                        'info': 'bg-blue-500/10 border-blue-500/30'
                      }[interp.type] || 'bg-[#0F172A] border-[#2D5A8F]';
                      
                      const textColor = {
                        'success': 'text-green-400',
                        'warning': 'text-yellow-400',
                        'error': 'text-red-400',
                        'info': 'text-blue-400'
                      }[interp.type] || 'text-gray-400';
                      
                      const IconComponent = {
                        'check': FaCheckCircle,
                        'alert': FaExclamationTriangle,
                        'warning': FaExclamationTriangle,
                        'shield': FaShieldAlt,
                        'trending-up': FaChartLine
                      }[interp.icon] || FaInfoCircle;
                      
                      return (
                        <div key={idx} className={`rounded-lg p-4 border ${bgColor}`}>
                          <div className="flex items-start gap-3">
                            <IconComponent className={`${textColor} mt-1`} />
                            <div>
                              <div className={`font-medium ${textColor}`}>{interp.title}</div>
                              <div className="text-gray-400 text-sm mt-1">{interp.message}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {taxData.recommendations?.length > 0 && (
                <div className="bg-gradient-to-r from-teal-500/10 to-green-500/10 rounded-xl p-4 border border-teal-500/20">
                  <h4 className="text-teal-400 font-bold mb-3 flex items-center gap-2">
                    <FaLightbulb />
                    Tax Saving Recommendations
                  </h4>
                  <div className="space-y-3">
                    {taxData.recommendations.map((rec, idx) => (
                      <div key={idx} className={`bg-[#0F172A] rounded-lg p-4 border ${
                        rec.priority === 'high' ? 'border-red-500/30' : 'border-[#2D5A8F]'
                      }`}>
                        <div className="flex items-start gap-3">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            rec.priority === 'high' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {rec.section}
                          </span>
                          <div className="flex-1">
                            <p className="text-white text-sm mb-2">{rec.message}</p>
                            <div className="space-y-1">
                              {rec.suggestions?.map((suggestion, sIdx) => (
                                <div key={sIdx} className="text-gray-400 text-xs flex items-center gap-2">
                                  <FaCheckCircle className="text-green-400" />
                                  {suggestion}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <FaFileInvoiceDollar className="text-4xl mx-auto mb-3 opacity-50" />
              <p>Save your budget to see tax awareness data</p>
            </div>
          )}
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveBudget}
          disabled={saving}
          className="px-8 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-xl hover:from-[#FFD700] hover:to-[#D4AF37] transition-all flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? (
            <>
              <FaSpinner className="animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <FaSave />
              Save Budget
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default MoneyMeter;
